import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchPublicMatchEngagement, fetchPublicMatchOnlineUsers, fetchPublicMatchScoreboard, followPublicMatch, getPublicMatchWatchLiveRedirectUrl, postPublicMatchChat, sendPublicMatchReaction, votePublicMatchPoll } from '../../../services/matchService';
import { normalizeBowlingOvers } from '../utils/overs';
import { deriveWinProbability } from '../utils/winProbability';
import { buildPlayerNameDirectory, isRawPlayerToken, resolvePlayerDisplayName } from '../utils/playerNameResolver';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const PUBLIC_VIEWER_STORAGE_KEY = 'crickarena-public-viewer-id';

export const PUBLIC_JACKPOT_OVER_SOURCE = 'score.currentOver.jackpotOver (fallback: score.currentOverJackpot only when currentOver is unavailable)';

const isTruthyJackpot = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const isJackpotDelivery = (event = {}) => {
  const extraType = String(event?.extraType || '').toLowerCase();
  const result = String(event?.result || '').toLowerCase();

  return Boolean(
    isTruthyJackpot(event?.jackpotOver) ||
    isTruthyJackpot(event?.isJackpotOver) ||
    isTruthyJackpot(event?.jackpot) ||
    extraType.includes('jackpot') ||
    result.includes('jackpot') ||
    result.includes('jp')
  );
};

export const derivePublicJackpotOverUiState = (score = {}, { overNumber = 1 } = {}) => {
  const hasAuthoritativeCurrentOver = score?.currentOver && typeof score.currentOver === 'object';
  const currentOver = hasAuthoritativeCurrentOver ? score.currentOver : {};
  const jackpotOver = score?.jackpotOver || {};
  const currentOverEvents = Array.isArray(currentOver?.events) ? currentOver.events : [];

  const hasEventJackpot = currentOverEvents.some(isJackpotDelivery);
  const authoritativeActive = Boolean(
    isTruthyJackpot(score?.isJackpotOver) ||
    isTruthyJackpot(currentOver?.jackpotOver) ||
    isTruthyJackpot(currentOver?.isJackpotOver) ||
    isTruthyJackpot(jackpotOver?.active) ||
    isTruthyJackpot(jackpotOver?.isActive)
  );
  const legacyActive = !hasAuthoritativeCurrentOver && isTruthyJackpot(score?.currentOverJackpot);

  const isJackpotOverActive = Boolean(authoritativeActive || legacyActive || hasEventJackpot);

  return {
    matchId: '',
    innings: Number(score?.currentInnings) || 1,
    overNumber: Number(overNumber) || 1,
    isJackpotOverActive,
    source: PUBLIC_JACKPOT_OVER_SOURCE,
    bannerVisible: isJackpotOverActive,
    badgeVisible: isJackpotOverActive,
  };
};



export const formatOverLabel = (overValue = null, event = null) => {
  const hasValue = (value) => value !== null && value !== undefined;
  const buildOverBall = (overNumber, ballValue) => {
    if (!hasValue(overNumber) || !hasValue(ballValue)) return null;
    return `${overNumber}.${ballValue}`;
  };

  const resolvedValue = hasValue(overValue)
    ? overValue
    : hasValue(event?.overBall)
      ? event.overBall
      : hasValue(event?.over)
        ? event.over
        : buildOverBall(event?.overNumber, event?.ballInOver) ?? buildOverBall(event?.overNumber, event?.ball) ?? '';

  return String(resolvedValue ?? '').trim();
};

const getPublicViewerId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const storedId = window.localStorage.getItem(PUBLIC_VIEWER_STORAGE_KEY);
  if (storedId) {
    return storedId;
  }

  const generatedId =
    window.crypto?.randomUUID?.() || `viewer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(PUBLIC_VIEWER_STORAGE_KEY, generatedId);
  return generatedId;
};

const oversFromBalls = (balls = 0) => {
  const safeBalls = Number(balls) || 0;
  if (safeBalls <= 0) {
    return '0.0';
  }
  const overs = Math.floor((safeBalls - 1) / 6);
  const remaining = ((safeBalls - 1) % 6) + 1;
  return `${overs}.${remaining}`;
};

const formatInningsLabel = (inningsNumber = 1) => {
  const n = Number(inningsNumber) || 1;
  const remainderHundred = n % 100;
  if (remainderHundred >= 11 && remainderHundred <= 13) return `${n}th Innings`;
  const remainderTen = n % 10;
  if (remainderTen === 1) return `${n}st Innings`;
  if (remainderTen === 2) return `${n}nd Innings`;
  if (remainderTen === 3) return `${n}rd Innings`;
  return `${n}th Innings`;
};

const getInningsKey = (innings, index) =>
  innings?.inningsId || innings?.id || `${index}-${innings?.battingTeamId || innings?.battingTeam || 'team'}`;

const normalizePowerPlays = (powerPlays = []) => {
  if (!Array.isArray(powerPlays)) return [];
  return powerPlays
    .map((entry) => ({
      startOver: Number(entry?.startOver),
      endOver: Number(entry?.endOver ?? entry?.startOver),
    }))
    .filter(
      (entry) =>
        Number.isFinite(entry.startOver) &&
        entry.startOver > 0 &&
        Number.isFinite(entry.endOver) &&
        entry.endOver > 0
    );
};

const formatPowerPlayLabel = (entry) =>
  entry.startOver === entry.endOver
    ? `Over ${entry.startOver}`
    : `Overs ${entry.startOver}-${entry.endOver}`;

const formatPowerPlaySummary = (powerPlays) =>
  (powerPlays || []).map(formatPowerPlayLabel).join(', ');

const parseDeliveryRuns = (result = '') => {
  const normalized = String(result).toLowerCase();
  if (!normalized) return 0;
  if (normalized.includes('w') && !normalized.match(/\d/)) return 0;
  const match = normalized.match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const getCelebrationType = (event) => {
  if (!event) return null;
  if (isActualWicket(event)) return 'wicket';
  const normalized = String(event.result || '').toLowerCase();
  if (normalized.includes('6') || normalized.includes('six')) return 'six';
  if (normalized.includes('4') || normalized.includes('four')) return 'four';
  return null;
};

const celebrationCopy = {
  six: { title: 'SIX!', subtitle: 'That one sailed over the ropes.' },
  four: { title: 'BOUNDARY!', subtitle: 'Pure timing to the fence.' },
  wicket: { title: 'WICKET!', subtitle: 'Huge breakthrough for the bowling side.' },
  bowler: { title: 'NEW BOWLER' },
  batter: { title: 'NEW BATTER' },
};

const celebrationIcons = {
  wicket: '🎯',
  six: '🚀',
  four: '⚡',
  bowler: '🎳',
  batter: '🏏',
};

const statusLabel = (s) => {
  const val = String(s || '').toLowerCase();
  if (!val) return 'UPCOMING';
  if (val === 'in_progress') return 'LIVE';
  if (val === 'completed' || val === 'complete' || val === 'finished') return 'COMPLETED';
  return val.replace(/_/g, ' ').toUpperCase();
};

const wicketTypeLabels = new Map([
  ['bowled', 'Bowled'],
  ['caught', 'Caught'],
  ['lbw', 'LBW'],
  ['run-out', 'Run out'],
  ['stumped', 'Stumped'],
  ['hit-wicket', 'Hit wicket'],
  ['retired-hurt', 'Retired hurt'],
  ['retired-out', 'Retired out'],
  ['obstructing-the-field', 'Obstructing the field'],
  ['hit-the-ball-twice', 'Hit the ball twice'],
  ['handled-ball', 'Handled the ball'],
  ['timed-out', 'Timed out'],
]);

const formatWicketTypeLabel = (wicketType = '') =>
  wicketTypeLabels.get(wicketType) || (wicketType ? wicketType.replace(/-/g, ' ') : '');
const getRetirementTypeFromEvent = (event) => {
  const wicketType = typeof event?.wicketType === 'string' ? event.wicketType : '';
  return wicketType.startsWith('retired') ? wicketType : '';
};
const isActualWicket = (event) => Boolean(event?.isWicket && !getRetirementTypeFromEvent(event));

const resolvePublicYouTubeWatchUrl = (info = {}) => {
  const directWatchUrl = [info.playbackUrl, info?.streaming?.playbackUrl]
    .find((url) => typeof url === 'string' && url.trim());

  const youtubeVideoId = (
    info.youtubeVideoId ||
    info?.streaming?.youtubeVideoId ||
    info?.streamingConfig?.youtube?.videoId ||
    ''
  ).trim();

  if (typeof directWatchUrl === 'string' && directWatchUrl.trim()) {
    return {
      youtubeVideoId,
      watchUrl: directWatchUrl.trim(),
    };
  }

  return {
    youtubeVideoId,
    watchUrl: youtubeVideoId ? `https://www.youtube.com/watch?v=${youtubeVideoId}` : '',
  };
};


const isInternalPlayerToken = (value) => isRawPlayerToken(value);

const resolveSafePublicPlayerName = (
  value,
  directory,
  fallback = 'Unknown player'
) => {
  const resolved = resolvePlayerDisplayName(value, directory, '');
  if (resolved && !isInternalPlayerToken(resolved)) return resolved;
  return fallback;
};

const getPublicRowPlayerName = (row, directory, fallback = 'Unknown player') => {
  const directName =
    row?.displayName ||
    row?.name ||
    row?.playerName ||
    row?.displayNameSnapshot ||
    row?.player?.displayName ||
    row?.player?.name ||
    row?.player?.displayNameSnapshot;
  if (directName && !isInternalPlayerToken(directName)) return directName;
  return resolveSafePublicPlayerName(
    row?.participantKey ||
      row?.playerId ||
      row?.id ||
      row?.player?.participantKey ||
      row?.player?.playerId,
    directory,
    fallback
  );
};

const normalizePublicWicketText = (value = '') => String(value || '').trim().toLowerCase().replace(/_/g, '-');

const getRetirementType = (batter) => {
  const dismissalType = normalizePublicWicketText(batter?.dismissalType);
  if (dismissalType === 'retired-hurt' || dismissalType === 'retired-out') return dismissalType;
  const retirementType = normalizePublicWicketText(batter?.retirementType);
  if (retirementType === 'retired-hurt' || retirementType === 'retired-out') return retirementType;
  const wicketType = normalizePublicWicketText(batter?.wicketType);
  if (wicketType === 'retired-hurt' || wicketType === 'retired-out') return wicketType;
  if (batter?.retiredHurt === true || batter?.isRetiredHurt === true) return 'retired-hurt';
  if (batter?.retiredOut === true || batter?.isRetiredOut === true) return 'retired-out';
  return '';
};

const normalizePlayerKey = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return `${value}`;
  if (typeof value === 'object') {
    return `${value.playerId || value.id || value._id || value.value || ''}`;
  }
  return '';
};

const getBatterDismissalEvent = (events = [], playerId = '') => {
  const normalizedPlayerId = normalizePlayerKey(playerId);
  if (!normalizedPlayerId) return null;

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (!event) continue;
    const eventPlayerId = normalizePlayerKey(event.playerOutId || event.batterOutId || event.strikerId || event.striker);
    if (eventPlayerId !== normalizedPlayerId) continue;
    if (getRetirementTypeFromEvent(event)) return event;
    if (isActualWicket(event)) return event;
  }

  return null;
};

export const derivePublicBattingRows = ({ batters = [], score = {}, events = [], inningsNumber, isMatchComplete = false }) => {
  const activeBatterIds = new Set(
    [score?.striker, score?.nonStriker]
      .map((value) => normalizePlayerKey(value))
      .filter(Boolean)
  );
  const isCurrentInnings = (Number(inningsNumber) || 1) === (Number(score?.currentInnings) || 1);
  const useLiveBattingState = isCurrentInnings && !isMatchComplete && activeBatterIds.size > 0;

  return batters.map((batter) => {
    const playerId = normalizePlayerKey(batter?.playerId || batter?.id || batter?._id);
    const dismissalEvent = getBatterDismissalEvent(events, playerId);
    const retirementType = getRetirementType(batter) || getRetirementTypeFromEvent(dismissalEvent);
    const isDismissed = Boolean(
      retirementType ||
      dismissalEvent ||
      batter?.isOut === true ||
      batter?.isOut === 1 ||
      batter?.dismissalType ||
      batter?.wicketType
    );
    const isActiveBatter = useLiveBattingState && activeBatterIds.has(playerId);

    if (isActiveBatter) {
      return {
        ...batter,
        derivedStatus: 'not-out',
        isOut: false,
        isRetiredHurt: false,
        retirementType: '',
      };
    }

    if (retirementType) {
      return {
        ...batter,
        derivedStatus: retirementType,
        dismissalType: batter.dismissalType || retirementType,
        wicketType: batter.wicketType || retirementType,
        isRetiredHurt: retirementType === 'retired-hurt',
        isOut: true,
        retirementType,
      };
    }

    if (useLiveBattingState && !isDismissed) {
      return {
        ...batter,
        derivedStatus: 'not-out',
        isRetiredHurt: false,
        isOut: false,
        retirementType: '',
      };
    }

    return {
      ...batter,
      derivedStatus: batter?.isOut === false || batter?.isOut === 0 ? 'not-out' : 'dismissed',
    };
  });
};

const chaseSummaryTerminalStatuses = new Set(['completed', 'complete', 'finished', 'all_out']);
const deriveBallsFromOversValue = (oversValue) => {
  if (oversValue === null || oversValue === undefined || oversValue === '') return null;
  if (typeof oversValue === 'number' && Number.isFinite(oversValue)) {
    const wholeOvers = Math.trunc(oversValue);
    const fractionalBalls = Math.round((oversValue - wholeOvers) * 10);
    return (wholeOvers * 6) + Math.min(5, Math.max(0, fractionalBalls));
  }

  const oversString = String(oversValue).trim();
  const match = oversString.match(/^(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const wholeOvers = Number(match[1]) || 0;
  const fractionalRaw = match[2] || '0';
  const fractionalBalls = Number(fractionalRaw[0]) || 0;
  return (wholeOvers * 6) + Math.min(5, Math.max(0, fractionalBalls));
};

const formatRequiredRunRate = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '0.00';
  return numericValue.toFixed(2);
};

export const getPublicTotalOvers = (match = {}, score = {}, summary = {}) =>
  match?.configuration?.overs
    ?? score?.overs
    ?? score?.maxOvers
    ?? score?.oversLimit
    ?? score?.totalOvers
    ?? summary?.oversLimit
    ?? '';

const pickFirstValidNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

export const deriveProjectedScore = (score = {}, match = {}, summary = {}) => {
  const currentInnings = Number(score?.currentInnings) || 1;
  const runs = Number(score?.runs);

  const ballsBowled = pickFirstValidNumber(
    score?.balls,
    deriveBallsFromOversValue(summary?.overs)
  );

  const oversLimit = pickFirstValidNumber(
    match?.configuration?.overs,
    score?.overs,
    score?.maxOvers,
    score?.oversLimit,
    score?.totalOvers,
    summary?.oversLimit
  );

  if (currentInnings !== 1) return null;
  if (!Number.isFinite(runs) || runs < 0) return null;
  if (!Number.isFinite(ballsBowled) || ballsBowled <= 0) return null;
  if (!Number.isFinite(oversLimit) || oversLimit <= 0) return null;

  const totalBalls = oversLimit * 6;
  if (totalBalls <= 0) return null;
  const projectedScoreVisibilityThreshold = totalBalls / 2;
  if (ballsBowled < projectedScoreVisibilityThreshold) return null;

  const projectedRuns = Math.round((runs / ballsBowled) * totalBalls);

  if (!Number.isFinite(projectedRuns) || projectedRuns < runs) return null;

  return projectedRuns;
};

export const deriveChaseSummary = ({ match = {}, score = {}, summary = {} } = {}) => {
  const currentInnings = Number(score?.currentInnings) || 1;
  const innings = Array.isArray(score?.innings) ? score.innings : [];
  const firstInnings =
    innings.find((inning) => Number(inning?.number) === 1)
    || innings.find((inning) => (Number(inning?.number) || 0) < currentInnings)
    || null;
  const derivedTarget = Number(firstInnings?.runs) + 1;
  const target = Number.isFinite(Number(summary?.target)) ? Number(summary.target) : derivedTarget;
  const currentRuns = Number(score?.runs) || 0;
  const ballsBowled = Number(score?.balls) || 0;
  const inningsOvers = Number(
    score?.overs
      ?? match?.configuration?.overs
      ?? score?.maxOvers
      ?? score?.oversLimit
      ?? score?.totalOvers
      ?? summary?.oversLimit
  );
  const maxBalls = Number.isFinite(inningsOvers) && inningsOvers > 0
    ? inningsOvers * 6
    : Number(score?.maxBalls);
  const scoreStatus = String(score?.status || summary?.status || '').toLowerCase();
  const isMatchComplete = summary?.isMatchComplete === true;
  const hasKnownTarget = Number.isFinite(target);
  const isChasingInnings = currentInnings > 1 && hasKnownTarget;
  const isActiveInnings = !chaseSummaryTerminalStatuses.has(scoreStatus);

  const debugPayload = {
    currentInnings,
    scoreStatus,
    scoreOvers: score?.overs ?? null,
    scoreBalls: score?.balls ?? null,
    innings,
    target: hasKnownTarget ? target : null,
    currentRuns,
    totalBalls: Number.isFinite(maxBalls) ? maxBalls : null,
    ballsBowled,
    ballsLeft: Number.isFinite(maxBalls) ? Math.max(maxBalls - ballsBowled, 0) : 0,
  };

  if (!isChasingInnings || !isActiveInnings || isMatchComplete) {
    return {
      show: false,
      runsNeeded: 0,
      ballsLeft: 0,
      requiredRunRate: '0.00',
    };
  }

  const runsNeeded = Math.max(target - currentRuns, 0);
  const ballsLeft = Number.isFinite(maxBalls) && maxBalls >= 0
    ? Math.max(maxBalls - ballsBowled, 0)
    : 0;
  const requiredRunRate =
    runsNeeded <= 0
      ? '0.00'
      : ballsLeft > 0
        ? formatRequiredRunRate((runsNeeded / ballsLeft) * 6)
        : '0.00';

  const result = {
    show: true,
    runsNeeded,
    ballsLeft,
    requiredRunRate,
  };


  return result;
};

const hasRoleFlag = (value) => value === true || value === 1 || String(value || '').toLowerCase() === 'true';

const extractRoleCodes = (roles = {}) => {
  if (!roles || typeof roles !== 'object') return [];
  const codes = [];
  if (hasRoleFlag(roles.captain) || hasRoleFlag(roles.isCaptain)) codes.push('C');
  if (hasRoleFlag(roles.viceCaptain) || hasRoleFlag(roles.isViceCaptain)) codes.push('VC');
  if (hasRoleFlag(roles.wicketKeeper) || hasRoleFlag(roles.isWicketKeeper)) codes.push('WK');
  return codes;
};

const ballChipType = (event) => {
  if (!event) return 'dot';
  if (isActualWicket(event)) return 'w';
  const extraType = String(event.extraType || '').toLowerCase();
  if (extraType) return 'extra';
  const numericRuns = Number(event.runs);
  const r = String(event.result || '').toLowerCase();
  if (r.includes('6')) return 'six';
  if (r.includes('4')) return 'four';
  if (r.includes('wide')) return 'extra';
  if (r.includes('no-ball') || r.includes('noball') || r.includes('nb')) return 'extra';
  if (r.includes('bye') || r.includes('leg bye') || r.includes('lb')) return 'extra';
  if (!r && Number.isFinite(numericRuns)) {
    if (numericRuns >= 6) return 'six';
    if (numericRuns === 4) return 'four';
    if (numericRuns === 0) return 'dot';
    return 'run';
  }
  const n = parseDeliveryRuns(event.result);
  if (n === 0) return 'dot';
  return 'run';
};

const compactBallLabel = (event) => {
  if (!event) return '-';
  const extraType = String(event.extraType || '').toLowerCase();
  if (extraType) {
    const totalRuns = Number(event.runs) || 0;
    if (extraType === 'wide') {
      const additional = Math.max(totalRuns - 1, 0);
      return additional > 0 ? `Wd+${additional}` : 'Wd';
    }
    if (extraType === 'no-ball') {
      const additional = Math.max(totalRuns - 1, 0);
      return additional > 0 ? `Nb+${additional}` : 'Nb';
    }
    if (extraType === 'bye') {
      return totalRuns ? `B${totalRuns}` : 'B';
    }
    if (extraType === 'leg-bye') {
      return totalRuns ? `Lb${totalRuns}` : 'Lb';
    }
    if (extraType === 'penalty') {
      return totalRuns ? `P${totalRuns}` : 'P';
    }
    if (extraType === 'jackpot-plus') {
      return totalRuns ? `JP${totalRuns}` : 'JP';
    }
    if (extraType === 'jackpot-minus') {
      const displayRuns = Math.abs(totalRuns);
      return displayRuns ? `${displayRuns}JM` : 'JM';
    }
  }
  const r = String(event.result || '').trim();
  if (!r) {
    if (event.isWicket) return 'W';
    const numericRuns = Number(event.runs);
    if (Number.isFinite(numericRuns)) return `${numericRuns}`;
    return '-';
  }
  // shorten common strings for compact chips
  const lower = r.toLowerCase();
  if (event.isWicket) return 'W';
  if (lower.includes('wide')) {
    const totalRuns = parseDeliveryRuns(r) || 1;
    const additional = totalRuns - 1;
    return additional > 0 ? `Wd+${additional}` : 'Wd';
  }
  if (lower.includes('no-ball') || lower.includes('noball') || lower.includes('nb')) {
    const totalRuns = parseDeliveryRuns(r) || 1;
    const additional = totalRuns - 1;
    return additional > 0 ? `Nb+${additional}` : 'Nb';
  }
  if (lower.includes('bye')) {
    const runs = parseDeliveryRuns(r);
    return runs ? `B${runs}` : 'B';
  }
  if (lower.includes('leg bye') || lower.includes('lb')) {
    const runs = parseDeliveryRuns(r);
    return runs ? `Lb${runs}` : 'Lb';
  }
  if (lower.includes('jackpot')) {
    if (lower.includes('plus')) return 'JP';
    if (lower.includes('minus')) return 'JM';
  }
  // If result already like "1 run" -> "1"
  const match = lower.match(/\d+/);
  if (match) return match[0];
  // fallback: first 2-3 chars
  return r.slice(0, 3);
};

const formatLastOverEventLabel = (event) => {
  if (!event) return '-';
  if (isActualWicket(event)) return 'W';
  if (getRetirementTypeFromEvent(event) === 'retired-hurt') return 'RH';

  const extraType = String(event.extraType || '').trim().toLowerCase();
  const totalRuns = Number(event.runs) || 0;
  if (extraType === 'wide') return totalRuns > 1 ? `WD${totalRuns}` : 'WD';
  if (extraType === 'no-ball') {
    const additional = Math.max(totalRuns - 1, 0);
    return additional > 0 ? `NB+${additional}` : 'NB';
  }
  if (extraType === 'bye') return totalRuns > 1 ? `${totalRuns}B` : 'B';
  if (extraType === 'leg-bye') return totalRuns > 1 ? `${totalRuns}LB` : 'LB';
  if (extraType === 'penalty') return totalRuns ? `P${totalRuns}` : 'P';
  if (extraType === 'jackpot-plus') return totalRuns ? `JP${totalRuns}` : 'JP';
  if (extraType === 'jackpot-minus') return totalRuns ? `${Math.abs(totalRuns)}JM` : 'JM';

  const numericRuns = Number(event.runs);
  if (Number.isFinite(numericRuns)) return `${numericRuns}`;

  return compactBallLabel(event).toUpperCase();
};

export const deriveCurrentPartnership = ({ currentPlayers = {}, score = {} } = {}) => {
  const partnership = currentPlayers?.partnership || null;
  const strikerId = score?.striker || currentPlayers?.batter?.playerId || '';
  const nonStrikerId = score?.nonStriker || currentPlayers?.nonStriker?.playerId || '';

  if (!partnership?.valid || !strikerId || !nonStrikerId) {
    return null;
  }

  return {
    batterIds: Array.isArray(partnership.batterIds) ? partnership.batterIds : [strikerId, nonStrikerId],
    runs: Number(partnership.runs) || 0,
    balls: Number(partnership.balls) || 0,
    hasStarted: partnership.hasStarted === true,
  };
};

export const deriveLastCompletedOverSummary = ({ events = [], inningsNumber } = {}) => {
  if (!Array.isArray(events) || events.length === 0) return null;

  const activeInningsNumber = Number(inningsNumber) || 1;
  const completedOvers = [];
  let currentOver = null;

  events.forEach((event = {}) => {
    const eventInningsNumber = Number(event?.inningsNumber) || activeInningsNumber;
    if (eventInningsNumber !== activeInningsNumber) return;

    if (!currentOver || currentOver.inningsNumber !== eventInningsNumber) {
      currentOver = {
        inningsNumber: eventInningsNumber,
        overNumber: 1,
        legalBalls: 0,
        runs: 0,
        events: [],
      };
    }

    currentOver.events.push(event);
    currentOver.runs += Number(event?.runs) || 0;
    if (event?.countBall) {
      currentOver.legalBalls += 1;
    }

    if (currentOver.legalBalls === 6) {
      completedOvers.push({
        inningsNumber: currentOver.inningsNumber,
        overNumber: currentOver.overNumber,
        runs: currentOver.runs,
        events: [...currentOver.events],
        labels: currentOver.events.map((delivery) => formatLastOverEventLabel(delivery)),
      });
      currentOver = {
        inningsNumber: eventInningsNumber,
        overNumber: currentOver.overNumber + 1,
        legalBalls: 0,
        runs: 0,
        events: [],
      };
    }
  });

  return completedOvers.length ? completedOvers[completedOvers.length - 1] : null;
};

const formatPublicInitials = (name = '') => {
  const cleaned = String(name || '').trim();
  if (!cleaned) return '';
  const lettersOnly = cleaned.replace(/[^a-zA-Z\s]/g, ' ').trim();
  const parts = lettersOnly.split(/\s+/).filter(Boolean);
  if (!parts.length) return cleaned.slice(0, 2).toUpperCase();
  const initials = parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
  return initials || cleaned.slice(0, 2).toUpperCase();
};

const formatExtraResult = (value = '') => {
  const text = String(value || '');
  if (!text) return '';
  const normalized = text.toLowerCase();
  const isAutomaticExtra =
    normalized.includes('wide') || normalized.includes('no-ball') || normalized.includes('noball') || normalized.includes('nb');
  if (!isAutomaticExtra) {
    return text;
  }

  const match = text.match(/(\+)?\s*(\d+)\b/);
  if (!match) return text;
  const total = Number(match[2]);
  if (!Number.isFinite(total)) return text;
  const additional = total - 1;
  if (additional <= 0) {
    return text.replace(/\s*\+?\s*\d+\b/, '').trim();
  }
  return text.replace(/(\+)?\s*\d+\b/, `+ ${additional}`);
};

const buildCurrentOverFromEvents = (events = [], ballsInOver = 0) => {
  if (events.length === 0) return null;

  const totalBallsInOver = Number(ballsInOver);
  if (!Number.isFinite(totalBallsInOver)) return null;

  let countedBalls = 0;
  const currentOverEvents = [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event?.countBall && countedBalls === totalBallsInOver) {
      break;
    }
    currentOverEvents.unshift(event);
    if (event?.countBall) {
      countedBalls += 1;
    }
  }

  return {
    ballsInOver: totalBallsInOver,
    remaining: 6 - totalBallsInOver,
    events: currentOverEvents,
  };
};

const getLastCompletedOverBowler = (events = []) => {
  let countedBalls = 0;
  let lastBowler = '';
  events.forEach((event) => {
    if (!event?.countBall) return;
    countedBalls += 1;
    if (countedBalls % 6 === 0) {
      lastBowler = event?.bowler || lastBowler;
    }
  });
  return lastBowler;
};


const formatPublicScoreLine = ({ runs = 0, wickets = 0, balls = 0, overs = '' } = {}) =>
  `${Number(runs) || 0}-${Number(wickets) || 0} (${overs || oversFromBalls(balls)})`;

const pickBestPartnership = (inningsStats = []) =>
  inningsStats
    .flatMap((innings = {}) =>
      ((innings.summary || {}).partnerships || []).map((partnership = {}) => ({
        ...partnership,
        battingTeam: innings.battingTeam,
        inningsNumber: innings.number,
      })),
    )
    .sort((a, b) => (Number(b.runs) || 0) - (Number(a.runs) || 0))[0] || null;

const getPublicMatchUrl = () => {
  if (typeof window === 'undefined') return '';
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
};

const timeAgo = (date) => {
  if (!date) return '';
  const diff = Math.max(Date.now() - new Date(date).getTime(), 0);
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
};


const REACTION_ITEMS = [
  { type: 'like', icon: '👍', label: 'Like' },
  { type: 'love', icon: '❤️', label: 'Love' },
  { type: 'fire', icon: '🔥', label: 'Fire' },
  { type: 'clap', icon: '👏', label: 'Clap' },
  { type: 'wow', icon: '😮', label: 'Wow' },
];

const eventIconForType = (type = '') => {
  const normalized = String(type).toLowerCase();
  if (normalized.includes('four') || normalized.includes('six') || normalized.includes('boundary')) return '⚡';
  if (normalized.includes('wicket')) return '🎯';
  if (normalized.includes('wide') || normalized.includes('no')) return '•';
  return '⚪';
};

const ViewerHeader = ({ viewerCount = 0 }) => (
  <div className="pmc-card-head pmc-viewer-head">
    <div>
      <div className="pmc-card-title">Viewer Engagement</div>
      <div className="pmc-muted small">Join the live match conversation</div>
    </div>
    <span className="pmc-live-badge"><span className="pmc-live-dot" />{viewerCount} Watching</span>
  </div>
);

const EngagementSummary = ({ viewerCount = 0, reactionsTotal = 0, followerCount = 0, messageCount = 0 }) => (
  <div className="pmc-engagement-summary" aria-label="Engagement summary">
    {[['Watching', viewerCount], ['Reactions', reactionsTotal], ['Followers', followerCount], ['Messages', messageCount]].map(([label, value]) => (
      <div className="pmc-engagement-stat" key={label}>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    ))}
  </div>
);

const ReactionCard = ({ reactions = {}, onReaction, pendingReaction }) => (
  <div className="pmc-engagement-card pmc-engagement-card--primary pmc-reaction-card">
    <div className="pmc-section-label">Reactions</div>
    <div className="pmc-reaction-pills" aria-label="Reactions">
      {REACTION_ITEMS.map(({ type, icon, label }) => (
        <button key={type} type="button" className={`pmc-reaction-pill ${pendingReaction === type ? 'is-active' : ''}`} disabled={Boolean(pendingReaction)} onClick={() => onReaction(type)} aria-label={`${label} reaction`}>
          <span>{icon}</span><strong>{reactions?.[type] ?? 0}</strong>
        </button>
      ))}
    </div>
    {Object.values(reactions || {}).some(Boolean) ? null : <div className="pmc-muted small mt-2">Be the first fan to react.</div>}
  </div>
);

const PollCard = ({ poll, onVote, pendingPollOption }) => {
  if (!poll) return <div className="pmc-engagement-card pmc-engagement-card--primary pmc-poll-card"><div className="pmc-section-label">Live Poll</div><div className="pmc-muted small">No active poll.</div></div>;
  const totalVotes = poll.totalVotes ?? (poll.options || []).reduce((sum, option) => sum + (option.votes || 0), 0);
  return (
    <div className="pmc-engagement-card pmc-engagement-card--primary pmc-poll-card">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2"><div className="pmc-section-label">Live Poll</div><span className={`pmc-poll-status ${poll.active ? 'is-open' : ''}`}>{poll.active ? 'Open' : (poll.status || 'Closed')}</span></div>
      <div className="pmc-poll-question">{poll.question}</div>
      <div className="pmc-muted small mb-2">{totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}</div>
      <div className="pmc-poll-options">
        {(poll.options || []).map((option) => {
          const percent = totalVotes ? Math.round(((option.votes || 0) / totalVotes) * 100) : 0;
          return (
            <button key={option.id} type="button" className="pmc-poll-option" disabled={!poll.active || Boolean(pendingPollOption)} onClick={() => onVote(poll.id, option.id)}>
              <span className="pmc-poll-option-top"><span>{option.text}</span><strong>{percent}%</strong></span>
              <span className="pmc-poll-track"><span style={{ width: `${percent}%` }} /></span>
              <span className="pmc-muted small">{option.votes || 0} votes</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FollowCard = ({ follow = {}, onFollow, pendingFollow }) => (
  <div className="pmc-engagement-card pmc-follow-card">
    <div><div className="pmc-section-label">🔔 Follow this match</div><div className="pmc-muted small">Get live score updates and notifications.</div><div className="pmc-follow-count">Followers {follow?.followerCount ?? 0}</div></div>
    <button type="button" className="btn btn-outline-primary btn-sm" disabled={pendingFollow} onClick={onFollow}>{follow?.following ? 'Following' : 'Follow'}</button>
  </div>
);

const ChatCard = ({ chat = [], chatText, setChatText, onSubmit, pendingChat }) => (
  <div className="pmc-engagement-card pmc-engagement-card--primary pmc-chat-card">
    <div className="pmc-section-label">💬 Fan Chat</div>
    <form onSubmit={onSubmit} className="pmc-chat-form"><input className="form-control form-control-sm" value={chatText} onChange={(event) => setChatText(event.target.value)} maxLength={300} placeholder="Type your message..." /><button className="btn btn-dark btn-sm" disabled={pendingChat || !chatText.trim()} type="submit">Send</button></form>
    <div className="pmc-chat-list">{chat.slice(-8).map((message) => <div key={message.id} className={`pmc-chat-message ${message.pinned ? 'is-pinned' : ''}`}><strong>{message.pinned ? 'Pinned • ' : ''}{message.displayName}</strong><span>{message.sanitizedText || message.text}</span></div>)}</div>
    {!chat.length ? <div className="pmc-muted small mt-2">No chat messages yet.</div> : null}
  </div>
);

const TimelineCard = ({ timeline = [] }) => (
  <div className="pmc-engagement-card pmc-engagement-card--secondary pmc-timeline-card"><div className="pmc-section-label">Match Timeline</div>{timeline.slice(0, 5).map((item) => <div key={item.id} className="pmc-timeline-item"><span>{eventIconForType(item.type)}</span><div><strong>{item.type}</strong><small>{item.label}{item.over ? ` • ${item.over}` : ''}</small></div></div>)}{!timeline.length ? <div className="pmc-muted small">Timeline will appear as key moments happen.</div> : null}{timeline.length > 5 ? <button type="button" className="pmc-link-button">View All →</button> : null}</div>
);

const BroadcastCard = ({ broadcast = {}, activeSponsorCard, sponsorWidgets = [] }) => (
  <div className="pmc-engagement-card pmc-engagement-card--secondary pmc-broadcast-card"><div className="pmc-section-label">🎥 Broadcast</div><div className="small">{broadcast?.currentlyLive ? 'Live now' : 'Not live'} • {broadcast?.replayAvailable ? 'Replay available' : 'Replay pending'}</div>{broadcast?.primaryReplayLink ? <a className="btn btn-link btn-sm p-0" href={broadcast.primaryReplayLink} target="_blank" rel="noreferrer">Open replay</a> : null}{activeSponsorCard ? <div className="pmc-sponsor-mini"><span>Sponsored</span><strong>{activeSponsorCard.creative?.sponsorName || activeSponsorCard.name}</strong><small>{activeSponsorCard.creative?.title}</small>{activeSponsorCard.creative?.imageUrl ? <img src={activeSponsorCard.creative.imageUrl} alt={activeSponsorCard.creative?.title || 'Sponsor'} className="img-fluid rounded mt-2" /> : null}{activeSponsorCard.creative?.qrData ? <small>QR: {activeSponsorCard.creative.qrData}</small> : null}</div> : null}{sponsorWidgets.map((widget) => <div key={widget.id} className="pmc-sponsor-mini"><strong>{widget.title}</strong><small>{widget.text}</small></div>)}</div>
);

const ViewerEngagementPanel = ({ engagement, onlineCount, engagementNotice, onReaction, onPollVote, onFollow, onChatSubmit, chatText, setChatText, activeSponsorCard, pendingReaction, pendingPollOption, pendingFollow, pendingChat }) => {
  const viewerCount = engagement?.presence?.currentViewers ?? onlineCount ?? 0;
  const reactionsTotal = REACTION_ITEMS.reduce((sum, item) => sum + (engagement?.reactions?.[item.type] || 0), 0);
  const chat = engagement?.chat || [];
  return (
    <div className="pmc-card pmc-viewer-engagement" aria-label="Viewer engagement"><ViewerHeader viewerCount={viewerCount} /><div className="pmc-card-body">{engagementNotice ? <div className="alert alert-info py-2 mb-3">{engagementNotice}</div> : null}<EngagementSummary viewerCount={viewerCount} reactionsTotal={reactionsTotal} followerCount={engagement?.follow?.followerCount ?? 0} messageCount={chat.length} /><div className="pmc-engagement-dashboard"><ReactionCard reactions={engagement?.reactions || {}} onReaction={onReaction} pendingReaction={pendingReaction} /><PollCard poll={(engagement?.polls || [])[0]} onVote={onPollVote} pendingPollOption={pendingPollOption} /><FollowCard follow={engagement?.follow} onFollow={onFollow} pendingFollow={pendingFollow} /><ChatCard chat={chat} chatText={chatText} setChatText={setChatText} onSubmit={onChatSubmit} pendingChat={pendingChat} /><TimelineCard timeline={engagement?.timeline || []} /><BroadcastCard broadcast={engagement?.broadcast} activeSponsorCard={activeSponsorCard} sponsorWidgets={engagement?.sponsorWidgets || []} /></div></div></div>
  );
};

const PublicMatchViewPage = ({ tournamentId: overrideTournamentId, matchId: overrideMatchId } = {}) => {
  const routeParams = useParams();
  const tournamentId = overrideTournamentId || routeParams.tournamentId;
  const matchId = overrideMatchId || routeParams.matchId;

  const [match, setMatch] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [liveScore, setLiveScore] = useState(null);
  const [scoreboardSummary, setScoreboardSummary] = useState(null);
  const [inningsSummaries, setInningsSummaries] = useState([]);
  const [publicView, setPublicView] = useState(null);

  const [activeTab, setActiveTab] = useState('live');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [onlineCount, setOnlineCount] = useState(null);
  const [onlineError, setOnlineError] = useState('');
  const [engagement, setEngagement] = useState(null);
  const [chatText, setChatText] = useState('');
  const [engagementNotice, setEngagementNotice] = useState('');
  const [pendingReaction, setPendingReaction] = useState('');
  const [pendingPollOption, setPendingPollOption] = useState('');
  const [pendingFollow, setPendingFollow] = useState(false);
  const [pendingChat, setPendingChat] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [celebration, setCelebration] = useState(null);
  const [forceOverReset, setForceOverReset] = useState(false);
  const isRefreshing = useRef(false);
  const celebrationTimer = useRef(null);
  const lastCelebrationKey = useRef('');
  const lastPlayersRef = useRef({ striker: '', nonStriker: '', bowler: '' });
  const lastWicketsRef = useRef(null);
  const pendingNewBatterRef = useRef(false);

  const celebrationStorageKey = tournamentId && matchId ? `public-celebration-${tournamentId}-${matchId}` : '';

  const loadPublicView = useCallback(
    async ({ withSpinner } = {}) => {
      if (!tournamentId || !matchId || isRefreshing.current) return;
      isRefreshing.current = true;

      if (withSpinner) setIsLoading(true);
      setError('');

      try {
        const response = await fetchPublicMatchScoreboard({ tournamentId, matchId });
        console.log('PUBLIC_MATCH_RUNTIME_CONTRACT_DEBUG', {
          tournamentId,
          matchId,
          responseKeys: Object.keys(response || {}),
          hasMatch: Boolean(response?.match),
          hasTournament: Boolean(response?.tournament),
          playersCount: Array.isArray(response?.players) ? response.players.length : -1,
          playerSamples: Array.isArray(response?.players) ? response.players.slice(0, 5) : response?.players,
          hasScore: Boolean(response?.score),
          scoreboardSummaryKeys: response?.scoreboardSummary && typeof response.scoreboardSummary === 'object' ? Object.keys(response.scoreboardSummary) : [],
          scoreboardBattingCount: response?.scoreboardSummary?.batting?.length ?? -1,
          scoreboardBowlingCount: response?.scoreboardSummary?.bowling?.length ?? -1,
          inningsSummariesCount: response?.inningsSummaries?.length ?? -1,
          publicViewKeys: response?.publicView && typeof response.publicView === 'object' ? Object.keys(response.publicView) : [],
          stats: response?.publicView?.stats,
          commentaryCount: response?.publicView?.commentary?.length ?? -1,
          team1SquadCount: response?.publicView?.squads?.team1?.players?.length ?? -1,
          team2SquadCount: response?.publicView?.squads?.team2?.players?.length ?? -1,
        });
        const activeInnings = response?.inningsSummaries?.[0] || null;
        console.log('GALI_PUBLIC_RENDER_CONTRACT_DEBUG', {
          inningsOvers: activeInnings?.overs,
          batting: activeInnings?.summary?.batting,
          bowling: activeInnings?.summary?.bowling,
          partnerships: activeInnings?.summary?.partnerships,
          commentaryCount: response?.publicView?.commentary?.length,
        });
        setMatch(response.match || null);
        setTournament(response.tournament || null);
        setPlayers(response.players || []);
        setLiveScore(response.score || response.match?.score || null);
        setScoreboardSummary(response.scoreboardSummary || null);
        setInningsSummaries(response.inningsSummaries || []);
        setPublicView(response.publicView || null);
        setLastUpdatedAt(new Date().toISOString());
      } catch (err) {
        setMatch(null);
        setTournament(null);
        setPlayers([]);
        setLiveScore(null);
        setScoreboardSummary(null);
        setInningsSummaries([]);
        setPublicView(null);
        setError(err.message || 'Unable to load public match view.');
      } finally {
        if (withSpinner) setIsLoading(false);
        isRefreshing.current = false;
      }
    },
    [matchId, tournamentId],
  );

  const showCelebration = useCallback((nextCelebration, duration = 2600) => {
    setCelebration(nextCelebration);
    if (celebrationTimer.current) clearTimeout(celebrationTimer.current);
    celebrationTimer.current = setTimeout(() => setCelebration(null), duration);
  }, []);

  useEffect(() => {
    loadPublicView({ withSpinner: true });
  }, [loadPublicView]);

  useEffect(() => {
    if (!celebrationStorageKey) return;
    const storedKey = localStorage.getItem(celebrationStorageKey);
    if (storedKey) lastCelebrationKey.current = storedKey;
  }, [celebrationStorageKey]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') loadPublicView();
    }, 15000);
    return () => clearInterval(intervalId);
  }, [loadPublicView]);

  useEffect(() => {
    if (!tournamentId || !matchId) return;
    const viewerId = getPublicViewerId();
    let isMounted = true;
    const refreshIntervalMs = 15000;

    const loadOnlineUsers = async () => {
      try {
        const response = await fetchPublicMatchOnlineUsers({ tournamentId, matchId, viewerId });
        if (!isMounted) return;
        setOnlineCount(response.count ?? 0);
        setOnlineError('');
      } catch (loadError) {
        if (!isMounted) return;
        setOnlineError(loadError.message || 'Unable to load online viewers.');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadOnlineUsers();
      }
    };

    loadOnlineUsers();
    const interval = setInterval(loadOnlineUsers, refreshIntervalMs);
    window.addEventListener('focus', loadOnlineUsers);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', loadOnlineUsers);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [matchId, tournamentId]);


  const viewerId = useMemo(() => getPublicViewerId(), []);

  const loadEngagement = useCallback(async () => {
    if (!tournamentId || !matchId) return;
    try {
      const response = await fetchPublicMatchEngagement({ tournamentId, matchId, viewerId });
      setEngagement(response.engagement || null);
    } catch (loadError) {
      setEngagementNotice(loadError.message || 'Viewer engagement is temporarily unavailable.');
    }
  }, [matchId, tournamentId, viewerId]);

  useEffect(() => {
    loadEngagement();
    const interval = setInterval(loadEngagement, 20000);
    return () => clearInterval(interval);
  }, [loadEngagement]);

  const handleReaction = async (type) => {
    setPendingReaction(type);
    try {
      const response = await sendPublicMatchReaction({ tournamentId, matchId, viewerId, type });
      setEngagement((current) => ({ ...(current || {}), reactions: response.reactions || {} }));
      setEngagementNotice('Reaction added.');
    } catch (reactionError) {
      setEngagementNotice(reactionError.message || 'Reaction throttled.');
    } finally {
      setPendingReaction('');
    }
  };

  const handlePollVote = async (pollId, optionId) => {
    setPendingPollOption(optionId);
    try {
      const response = await votePublicMatchPoll({ tournamentId, matchId, pollId, optionId, viewerId });
      setEngagement((current) => ({ ...(current || {}), polls: (current?.polls || []).map((poll) => (poll.id === pollId ? response.poll : poll)) }));
      setEngagementNotice('Vote recorded.');
    } catch (voteError) {
      setEngagementNotice(voteError.message || 'Vote already recorded.');
    } finally {
      setPendingPollOption('');
    }
  };

  const handleChatSubmit = async (event) => {
    event.preventDefault();
    if (!chatText.trim()) return;
    setPendingChat(true);
    try {
      const response = await postPublicMatchChat({ tournamentId, matchId, viewerId, displayName: 'Viewer', text: chatText });
      setChatText('');
      setEngagement((current) => ({ ...(current || {}), chat: [...(current?.chat || []), response.message].slice(-50) }));
    } catch (chatError) {
      setEngagementNotice(chatError.message || 'Unable to send chat message.');
    } finally {
      setPendingChat(false);
    }
  };

  const handleFollow = async () => {
    setPendingFollow(true);
    try {
      const nextFollowing = !engagement?.follow?.following;
      const response = await followPublicMatch({ tournamentId, matchId, viewerId, following: nextFollowing });
      setEngagement((current) => ({ ...(current || {}), follow: response.follow }));
    } finally {
      setPendingFollow(false);
    }
  };

  const playerDirectory = useMemo(() => buildPlayerNameDirectory(players || []), [players]);

  const resolvePlayerName = (playerId, fallback = '') => resolveSafePublicPlayerName(playerId, playerDirectory, fallback);

  const formatDismissal = useCallback(
    (batter) => {
      if (!batter) return '';
      const retirementType = getRetirementType(batter);
      if (retirementType) return formatWicketTypeLabel(retirementType).toLowerCase();
      const isNotOut = batter.derivedStatus === 'not-out' || batter.isOut === false || batter.isOut === 0;
      if (isNotOut) return 'not out';

      const wicketType = batter.dismissalType || batter.wicketType || '';
      const bowlerName = resolvePlayerName(batter.wicketBowler, '') || '';
      const fielderName = resolvePlayerName(batter.wicketFielder, '') || '';
      const wicketLabel = formatWicketTypeLabel(wicketType);

      switch (wicketType) {
        case 'caught': {
          const fielderLabel = fielderName || 'fielder';
          return `c ${fielderLabel}${bowlerName ? ` b ${bowlerName}` : ''}`;
        }
        case 'stumped': {
          const fielderLabel = fielderName || 'keeper';
          return `st ${fielderLabel}${bowlerName ? ` b ${bowlerName}` : ''}`;
        }
        case 'run-out':
          return fielderName ? `run out (${fielderName})` : 'run out';
        case 'bowled':
          return bowlerName ? `b ${bowlerName}` : 'bowled';
        case 'lbw':
          return bowlerName ? `lbw b ${bowlerName}` : 'lbw';
        case 'hit-wicket':
          return bowlerName ? `hit wicket b ${bowlerName}` : 'hit wicket';
        default:
          if (wicketLabel) return wicketLabel;
          if (bowlerName) return `b ${bowlerName}`;
          return 'out';
      }
    },
    [resolvePlayerName],
  );

  const renderPlayerNameWithRoles = (player, fallbackId, roleCodes = []) => {
    let resolvedName = '';
    if (!player) {
      resolvedName = resolvePlayerName(fallbackId, '') || '';
    } else if (typeof player === 'string' || typeof player === 'number') {
      resolvedName = resolvePlayerName(player, '') || '';
    } else if (typeof player.name === 'string') {
      resolvedName = player.name;
    } else if (player.name && typeof player.name === 'object') {
      if (typeof player.name.full === 'string') {
        resolvedName = player.name.full;
      } else if (typeof player.name.first === 'string' || typeof player.name.last === 'string') {
        resolvedName = `${player.name.first || ''} ${player.name.last || ''}`.trim();
      }
    }

    if (!resolvedName) {
      resolvedName = resolvePlayerName(player?.participantKey || player?.playerId || player?.id || player?._id || fallbackId, '') || '';
    }

    const baseName = resolvedName || 'Player';
    const normalizedRoles = roleCodes.length ? roleCodes : extractRoleCodes(player?.roles);

    if (!normalizedRoles.length) {
      return <>{baseName}</>;
    }

    return (
      <>
        {baseName}
        {normalizedRoles.includes('C') ? (
          <span className="role-badge role-c" aria-label="Captain">C</span>
        ) : null}
        {normalizedRoles.includes('VC') ? (
          <span className="role-badge role-vc" aria-label="Vice Captain">VC</span>
        ) : null}
        {normalizedRoles.includes('WK') ? (
          <span className="role-badge role-wk" aria-label="Wicket Keeper">WK</span>
        ) : null}
      </>
    );
  };

  const getPlayerId = (player) => {
    if (!player) return null;
    if (typeof player === 'string' || typeof player === 'number') return `${player}`;
    if (player.playerId) return `${player.playerId}`;
    if (player.id) return `${player.id}`;
    if (player._id) return `${player._id}`;
    if (player.value) return `${player.value}`;
    return null;
  };

  // Use the top-level public scoreboard payload as the live scoring source of truth.
  // `match.score` is part of the schedule-shaped match payload and can lag behind
  // scorer state for striker/non-striker/bowler after wickets, bowler changes,
  // and innings transitions.
  const score = liveScore || match?.score || {};
  const summary = publicView?.summary || {};
  const dlsProjection = publicView?.summary?.dls || match?.dls || null;
  const chaseComparison = summary?.chaseComparison || score?.chaseComparison || null;
  const totalOver = getPublicTotalOvers(match, score, summary);
  const currentPlayers = summary.currentPlayers || {};
  const currentBatter = currentPlayers.batter || null;
  const currentNonStriker = currentPlayers.nonStriker || null;
  const currentBowler = currentPlayers.bowler || null;
  const currentPartnership = useMemo(
    () => deriveCurrentPartnership({ currentPlayers, score }),
    [currentPlayers, score],
  );
  const currentStrikerId = getPlayerId(currentBatter) || score?.striker || '';
  const currentNonStrikerId = getPlayerId(currentNonStriker) || score?.nonStriker || '';
  const currentBowlerId = getPlayerId(currentBowler);
  const stats = publicView?.stats || {};
  const commentary = useMemo(() => publicView?.commentary || [], [publicView?.commentary]);

  const scoreEvents = useMemo(() => score?.events || [], [score?.events]);
  const totalBalls = Number(score?.balls) || 0;
  const ballsInOver = totalBalls % 6;
  const configuredPowerPlayStart = Number(match?.configuration?.powerPlayStartOver);
  const configuredPowerPlayEnd = Number(match?.configuration?.powerPlayEndOver);
  const fallbackPowerPlay = useMemo(
    () =>
      Number.isFinite(configuredPowerPlayStart) && configuredPowerPlayStart > 0
        ? [
            {
              startOver: configuredPowerPlayStart,
              endOver:
                Number.isFinite(configuredPowerPlayEnd) && configuredPowerPlayEnd > 0
                  ? configuredPowerPlayEnd
                  : configuredPowerPlayStart,
            },
          ]
        : [],
    [configuredPowerPlayEnd, configuredPowerPlayStart]
  );
  const powerPlays = useMemo(() => {
    const saved = normalizePowerPlays(score?.powerPlays);
    return saved.length ? saved : fallbackPowerPlay;
  }, [fallbackPowerPlay, score?.powerPlays]);
  const safeBalls = Number(totalBalls) || 0;

  const currentOverNumber =
    safeBalls === 0
      ? 1
      : Math.floor((safeBalls - 1) / 6) + 1;

  const hasPowerPlay = Array.isArray(powerPlays) && powerPlays.length > 0;

  const jackpotOverUiState = useMemo(
    () => ({
      ...derivePublicJackpotOverUiState(score, { overNumber: currentOverNumber }),
      matchId,
    }),
    [currentOverNumber, matchId, score],
  );
  const isJackpotOverActive = jackpotOverUiState.isJackpotOverActive;

  const isPowerPlayOver =
    hasPowerPlay &&
    powerPlays.some(
      (entry) =>
        currentOverNumber >= entry.startOver && currentOverNumber <= entry.endOver
    );
  const currentInnings = Number(score?.currentInnings) || 1;
  const targetRuns = Number(summary?.target);
  const chaseSummary = useMemo(() => deriveChaseSummary({ match, score, summary }), [match, score, summary]);
  const projectedScore = useMemo(() => deriveProjectedScore(score, match, summary), [match, score, summary]);
  const playersPerSide = Number(
    match?.configuration?.playingCount ?? match?.configuration?.playersPerSide
    ?? score?.playersPerSide
    ?? summary?.playersPerSide
    ?? 11
  );
  const winProbability = useMemo(() => deriveWinProbability({
    currentInnings,
    battingTeam: summary?.battingTeam || score?.battingTeam || match?.team1 || 'Chasing team',
    bowlingTeam: summary?.bowlingTeam || score?.bowlingTeam || match?.team2 || 'Fielding team',
    target: Number.isFinite(targetRuns) ? targetRuns : summary?.target,
    currentRuns: score?.runs,
    ballsLeft: chaseSummary?.ballsLeft,
    wicketsDown: score?.wickets,
    wicketsRemaining: Math.max(playersPerSide - 1 - (Number(score?.wickets) || 0), 0),
    oversFormat: match?.configuration?.overs ?? score?.overs ?? summary?.oversLimit,
    playersPerSide,
    status: score?.status || summary?.status,
    isMatchComplete: summary?.isMatchComplete === true,
  }), [chaseSummary?.ballsLeft, currentInnings, match?.configuration?.overs, match?.configuration?.playingCount ?? match?.configuration?.playersPerSide, match?.team1, match?.team2, playersPerSide, score?.battingTeam, score?.bowlingTeam, score?.runs, score?.status, score?.wickets, summary?.battingTeam, summary?.bowlingTeam, summary?.isMatchComplete, summary?.oversLimit, summary?.status, summary?.target, targetRuns]);
  const isInningsBreak =
    currentInnings === 1 &&
    (score?.status === 'completed' || score?.status === 'all_out') &&
    !summary.isMatchComplete;
  const showTarget = (currentInnings > 1 || isInningsBreak) && Number.isFinite(targetRuns);
  const lastOverBowler = useMemo(() => getLastCompletedOverBowler(scoreEvents), [scoreEvents]);
  const derivedCurrentOver = useMemo(
    () => buildCurrentOverFromEvents(scoreEvents, ballsInOver),
    [ballsInOver, scoreEvents],
  );
  const defaultOver = { events: [], ballsInOver, remaining: 6 - ballsInOver };
  const currentOver =
    score?.currentOver?.events?.length > 0 ? score.currentOver : derivedCurrentOver || score?.currentOver || defaultOver;
  const currentOverEvents = currentOver.events || [];
  const currentOverBallCount = Number(currentOver?.ballsInOver);
  const currentOverBallTotal = Number.isFinite(currentOverBallCount) ? currentOverBallCount : ballsInOver;
  const displayOvers = useMemo(() => {
    const baseOvers = summary.overs || oversFromBalls(score.balls);
    if (!totalBalls || totalBalls % 6 !== 0) return baseOvers;
    const activeBowler = currentBowlerId || score.bowler;
    if (!activeBowler || !lastOverBowler || activeBowler === lastOverBowler) {
      return baseOvers;
    }
    const completedOvers = Math.floor(totalBalls / 6);
    return `${completedOvers}.0`;
  }, [currentBowlerId, lastOverBowler, score.balls, score.bowler, summary.overs, totalBalls]);
  const info = publicView?.info || {};
  const squads = publicView?.squads || {};
  const roleDirectory = useMemo(() => {
    const entries = [];
    const allSquadPlayers = [
      ...(squads.team1?.players || []),
      ...(squads.team2?.players || []),
    ];
    allSquadPlayers.forEach((player) => {
      const playerId = player?.id?.toString?.() || player?.id;
      if (!playerId) return;
      entries.push([playerId, extractRoleCodes(player.roles)]);
    });
    return new Map(entries);
  }, [squads.team1?.players, squads.team2?.players]);
  const analysis = publicView?.analysis || {};
  const mvp = publicView?.mvp || null;
  const awards = publicView?.awards || null;
  const activeSponsorCard = publicView?.activeSponsorCard || null;

  const streamUrl = info.streamUrl?.trim?.() || '';
  const normalizedStreamProvider = (info.streamProvider || 'youtube').toLowerCase();
  const streamProvider = ['local', 'local_preview'].includes(normalizedStreamProvider)
    ? 'local'
    : 'youtube';
  const { youtubeVideoId, watchUrl: resolvedYouTubeWatchUrl } = resolvePublicYouTubeWatchUrl(info);
  const livePlaybackUrl = (info.livePlaybackUrl || '').trim();
  const localStreamKey = (info.rtmpStreamKey || '').trim();
  const localHlsUrl = localStreamKey ? `http://127.0.0.1:8080/live/${localStreamKey}/index.m3u8` : livePlaybackUrl;
  const youtubeEmbedUrl = youtubeVideoId ? `https://www.youtube.com/embed/${youtubeVideoId}` : '';
  const watchUrl = streamProvider === 'local' ? (localHlsUrl || streamUrl) : resolvedYouTubeWatchUrl;
  const dynamicWatchRedirectUrl = tournamentId && matchId ? getPublicMatchWatchLiveRedirectUrl({ tournamentId, matchId }) : '';
  const isMatchComplete = Boolean(summary.isMatchComplete);
  const isMatchCompleted =
    isMatchComplete ||
    match?.status === 'completed' ||
    match?.matchStatus === 'completed' ||
    match?.state === 'completed';
  const resultText = isMatchCompleted ? (summary?.result || match?.result || '') : '';


  const battingSummary = useMemo(() => scoreboardSummary?.batting || [], [scoreboardSummary?.batting]);

  const runBreakdown = useMemo(() => {
    const totalRuns = Number(score.runs) || 0;
    const totalFours = battingSummary.reduce((sum, batter) => sum + (Number(batter.fours) || 0), 0);
    const totalSixes = battingSummary.reduce((sum, batter) => sum + (Number(batter.sixes) || 0), 0);
    const boundaryRuns = totalFours * 4 + totalSixes * 6;

    const extras = scoreboardSummary?.extras || stats.extras || {};
    const extrasTotal = Number(extras.total) || 0;

    const otherRuns = Math.max(totalRuns - boundaryRuns - extrasTotal, 0);
    const total = Math.max(boundaryRuns + otherRuns + extrasTotal, 1);

    return {
      totalRuns,
      boundaryRuns,
      extrasTotal,
      otherRuns,
      total,
      totalFours,
      totalSixes,
    };
  }, [battingSummary, score.runs, scoreboardSummary?.extras, stats.extras]);

  const currentOverSummary = useMemo(
    () => ({
      ballsInOver: currentOverBallTotal,
      runsInOver: currentOverEvents.reduce((sum, event) => sum + (Number(event?.runs) || 0), 0),
    }),
    [currentOverBallTotal, currentOverEvents],
  );

  const currentOverDeliveries = useMemo(() => {
    if (forceOverReset && currentOverBallTotal === 0) return [];
    return currentOverEvents;
  }, [currentOverBallTotal, currentOverEvents, forceOverReset]);

  const lastCompletedOver = useMemo(
    () => deriveLastCompletedOverSummary({ events: scoreEvents, inningsNumber: currentInnings }),
    [currentInnings, scoreEvents],
  );

  const inningsStats = useMemo(() => {
    if (Array.isArray(inningsSummaries) && inningsSummaries.length > 0) return inningsSummaries;
    if (scoreboardSummary) {
      return [
        {
          number: Number(score?.currentInnings) || 1,
          battingTeam: score?.battingTeam,
          bowlingTeam: score?.bowlingTeam,
          runs: score?.runs || 0,
          wickets: score?.wickets || 0,
          balls: score?.balls || 0,
          extras: score?.extras || {},
          status: score?.status,
          summary: scoreboardSummary,
          totalOver: totalOver,
        },
      ];
    }
    return [];
  }, [inningsSummaries, score, scoreboardSummary]);

  const inningsKeys = useMemo(() => inningsStats.map((innings, index) => getInningsKey(innings, index)), [inningsStats]);
  const [openInningsKey, setOpenInningsKey] = useState(null);

  useEffect(() => {
    if (!inningsKeys.length) {
      setOpenInningsKey(null);
      return;
    }

    setOpenInningsKey((previousKey) => {
      if (previousKey && inningsKeys.includes(previousKey)) {
        return previousKey;
      }
      return inningsKeys[0];
    });
  }, [inningsKeys]);

  const toggleInningsAccordion = useCallback((inningsKey) => {
    setOpenInningsKey((previousKey) => (previousKey === inningsKey ? null : inningsKey));
  }, []);

  const boundaryPct = useMemo(() => Math.round((runBreakdown.boundaryRuns / runBreakdown.total) * 100), [runBreakdown]);
  const extrasPct = useMemo(() => Math.round((runBreakdown.extrasTotal / runBreakdown.total) * 100), [runBreakdown]);
  const otherPct = useMemo(() => Math.max(100 - boundaryPct - extrasPct, 0), [boundaryPct, extrasPct]);

  const runCompositionStyle = useMemo(
    () => ({
      background: `conic-gradient(var(--pmc-accent) 0 ${boundaryPct}%, var(--pmc-danger) ${boundaryPct}% ${
        boundaryPct + extrasPct
      }%, var(--pmc-gold) ${boundaryPct + extrasPct}% 100%)`,
    }),
    [boundaryPct, extrasPct],
  );

  const topBatters = useMemo(() => {
    const source = stats.topBatters?.length ? stats.topBatters : battingSummary;
    return (source || [])
      .map((batter) => ({
        name: getPublicRowPlayerName(batter, playerDirectory, 'Unknown player'),
        runs: Number(batter.runs) || 0,
      }))
      .filter((batter) => batter.runs >= 0)
      .sort((a, b) => b.runs - a.runs)
      .slice(0, 4);
  }, [battingSummary, playerDirectory, stats.topBatters]);

  const maxBatterRuns = Math.max(...topBatters.map((b) => b.runs), 1);

  const wicketPct = Math.min(((Number(score.wickets) || 0) / 10) * 100, 100);

  const runRateValue = Number(summary.runRate) || 0;
  const runRateMax = 12;
  const runRatePct = Math.min((runRateValue / runRateMax) * 100, 100);

  const recentEvents = useMemo(() => (commentary || []).slice(0, 8).reverse(), [commentary]);
  const recentDeliveries = useMemo(
    () =>
      (commentary || [])
        .slice(0, 8)
        .map((event) => ({
          label: event.over || '',
          result: event.result || '',
          runs: parseDeliveryRuns(event.result),
          isWicket: event.isWicket,
        }))
        .reverse(),
    [commentary],
  );

  useEffect(() => {
    const liveTabPayload = { liveScore: score, scoreboardSummary, inningsSummaries, publicViewSummary: publicView?.summary, publicViewStats: publicView?.stats, publicViewCommentary: publicView?.commentary, publicViewLive: publicView?.live };
    const widgetAudit = {
      LiveTab: liveTabPayload,
      CurrentBattersCard: {
        input: publicView?.summary?.currentPlayers,
        mapper: 'publicView.summary.currentPlayers -> currentBatter/currentNonStriker',
        renderedProps: { striker: currentBatter, nonStriker: currentNonStriker },
      },
      CurrentBowlerCard: {
        input: publicView?.summary?.currentPlayers?.bowler,
        mapper: 'publicView.summary.currentPlayers.bowler -> currentBowler',
        renderedProps: { bowler: currentBowler, currentOver: currentOverSummary },
      },
      RecentDeliveriesCard: {
        input: { commentary, recentDeliveries, timeline: score?.timeline, lastBalls: score?.lastBalls, ballEvents: score?.events },
        mapper: 'publicView.commentary.slice(0, 8).reverse()',
        renderedProps: { recentEvents },
      },
      RunCompositionCard: {
        input: { score, scoreboardSummary, stats },
        mapper: 'score.runs + scoreboardSummary.batting boundaries + extras',
        renderedProps: runBreakdown,
      },
      QuickStatsCard: {
        input: { summary, score, publicViewLive: publicView?.live },
        mapper: 'publicView.summary.runRate / chaseSummary / projectedScore',
        renderedProps: { currentRunRate: summary.runRate, requiredRunRate: chaseSummary.requiredRunRate, projectedScore },
      },
      counts: {
        publicViewCommentary: commentary.length,
        recentDeliveries: recentDeliveries.length,
        timeline: Array.isArray(score?.timeline) ? score.timeline.length : 0,
        BallEvents: Array.isArray(score?.events) ? score.events.length : 0,
      },
    };
    console.log('PUBLIC_MATCH_LIVE_RUNTIME_JSON', JSON.stringify({ matchType: match?.matchType || tournament?.id || tournamentId, liveTabPayload, widgetAudit }, null, 2));
  }, [chaseSummary.requiredRunRate, commentary, currentBatter, currentBowler, currentNonStriker, currentOverSummary, inningsSummaries, match?.matchType, projectedScore, publicView, recentDeliveries, recentEvents, runBreakdown, score, scoreboardSummary, stats, summary, tournament?.id, tournamentId]);

  const maxDeliveryRuns = Math.max(...recentDeliveries.map((entry) => entry.runs), 1);

  useEffect(() => {
    console.log('PUBLIC_JACKPOT_OVER_UI_DEBUG', jackpotOverUiState);
  }, [jackpotOverUiState]);

  const finalScores = useMemo(
    () =>
      inningsStats.map((innings = {}) => ({
        team: innings.battingTeam || innings.battingTeamShortName || 'Team',
        score: formatPublicScoreLine(innings),
        runs: Number(innings.runs) || 0,
        wickets: Number(innings.wickets) || 0,
      })),
    [inningsStats],
  );
  const topBatter = useMemo(() => {
    const source = stats.topBatters?.length ? stats.topBatters : inningsStats.flatMap((innings = {}) => innings.summary?.batting || []);
    return [...source].sort((a, b) => (Number(b.runs) || 0) - (Number(a.runs) || 0))[0] || null;
  }, [inningsStats, stats.topBatters]);
  const topBowler = useMemo(() => {
    const source = stats.topBowlers?.length ? stats.topBowlers : inningsStats.flatMap((innings = {}) => innings.summary?.bowling || []);
    return [...source].sort((a, b) => {
      const wicketDiff = (Number(b.wickets) || 0) - (Number(a.wickets) || 0);
      if (wicketDiff !== 0) return wicketDiff;
      return (Number(a.runsConceded) || 0) - (Number(b.runsConceded) || 0);
    })[0] || null;
  }, [inningsStats, stats.topBowlers]);
  const bestPartnership = useMemo(() => pickBestPartnership(inningsStats), [inningsStats]);
  const aiNarrativeSummary = useMemo(() => {
    const teamA = match?.team1 || squads.team1?.name || 'Team A';
    const teamB = match?.team2 || squads.team2?.name || 'Team B';

    if (!isMatchCompleted) {
      return 'AI match summary will be generated after match completion.';
    }

    const resolveName = (entry) => getPublicRowPlayerName(entry, playerDirectory, '');
    const scoreLines = finalScores
      .filter((item) => item?.team || item?.score)
      .map((item) => `${item.team || 'Team'} ${item.score || 'score unavailable'}`);
    const inningsStories = inningsStats.map((innings = {}, index) => {
      const inningsSummary = innings.summary || {};
      const teamName = innings.battingTeam || innings.battingTeamShortName || `Innings ${index + 1}`;
      const scoreLine = formatPublicScoreLine(innings);
      const inningsBatters = [...(inningsSummary.batting || [])]
        .filter((batter) => resolveName(batter))
        .sort((a, b) => (Number(b.runs) || 0) - (Number(a.runs) || 0))
        .slice(0, 2);
      const inningsBowlers = [...(inningsSummary.bowling || [])]
        .filter((bowler) => resolveName(bowler))
        .sort((a, b) => {
          const wicketDiff = (Number(b.wickets) || 0) - (Number(a.wickets) || 0);
          if (wicketDiff !== 0) return wicketDiff;
          return (Number(a.runsConceded) || 0) - (Number(b.runsConceded) || 0);
        })
        .slice(0, 2);
      const battingText = inningsBatters.length
        ? inningsBatters
            .map((batter) => `${resolveName(batter)} (${Number(batter.runs) || 0}${Number(batter.balls) ? ` off ${Number(batter.balls)}` : ''})`)
            .join(', ')
        : '';
      const bowlingText = inningsBowlers.length
        ? inningsBowlers
            .map((bowler) => `${resolveName(bowler)} (${Number(bowler.wickets) || 0}/${Number(bowler.runsConceded ?? bowler.runs) || 0})`)
            .join(', ')
        : '';

      return [
        `${index === 0 ? '🔥' : '🎯'} ${teamName} – ${index === 0 ? 'Batting Brilliance' : 'The Chase'}`,
        `${teamName} finished on ${scoreLine}${battingText ? `, powered by ${battingText}` : ''}${bowlingText ? `. With the ball, ${bowlingText} kept the pressure on` : ''}.`,
      ].join('\n');
    });

    const fallOfWickets = inningsStats.flatMap((innings = {}) =>
      (innings.summary?.fallOfWickets || []).map((entry = {}) => ({ ...entry, battingTeam: innings.battingTeam })),
    );
    const firstFow = fallOfWickets[0];
    const lastFow = fallOfWickets[fallOfWickets.length - 1];
    const playerOfMatch = awards?.manOfTheMatch || mvp || null;
    const playerOfMatchName = resolveName(playerOfMatch);
    const topBatterName = resolveName(topBatter);
    const topBowlerName = resolveName(topBowler);
    const bestPartnershipPlayers = bestPartnership
      ? [resolvePlayerName(bestPartnership.batter1), resolvePlayerName(bestPartnership.batter2)].filter(Boolean)
      : [];
    const bestPartnershipText = bestPartnership && bestPartnershipPlayers.length
      ? `${Number(bestPartnership.runs) || 0} runs by ${bestPartnershipPlayers.join(' & ')}`
      : '';
    const tossText = summary.toss || score?.toss || '';
    const highlights = [
      topBatterName ? `Top innings: ${topBatterName} scored ${Number(topBatter?.runs) || 0}.` : '',
      bestPartnershipText ? `Big partnership: ${bestPartnershipText}.` : '',
      topBowlerName ? `Key wickets: ${topBowlerName} took ${Number(topBowler?.wickets) || 0}/${Number(topBowler?.runsConceded ?? topBowler?.runs) || 0}.` : '',
      lastFow ? `Fall of wickets: from ${firstFow?.battingTeam || 'innings'} ${firstFow?.runs}-${firstFow?.wicket} to ${lastFow?.battingTeam || 'innings'} ${lastFow?.runs}-${lastFow?.wicket}.` : '',
      resultText ? `Result: ${resultText}.` : '',
    ].filter(Boolean);

    return [
      `🏏✨ Match Summary – ${teamA} vs ${teamB} ✨🏏`,
      '',
      `${tossText ? `${tossText} ` : ''}${scoreLines.length ? `The match wrapped up with ${scoreLines.join(' and ')}.` : 'The match reached its finish with the scorecard telling the full story.'}`,
      '',
      ...inningsStories.flatMap((story) => [story, '']),
      '⭐ Match Highlights',
      ...(highlights.length ? highlights.map((item) => `- ${item}`) : ['- Scorecard highlights were not available in the public data.']),
      '',
      '👑 Player of the Match',
      playerOfMatchName
        ? `${playerOfMatchName}${Number(playerOfMatch?.runs) || Number(playerOfMatch?.wickets) ? ` – ${Number(playerOfMatch?.runs) || 0} runs, ${Number(playerOfMatch?.wickets) || 0} wickets.` : ' – listed on the official scorecard.'}`
        : 'Not announced yet.',
      '',
      `What a finish! ${resultText || `${teamA} vs ${teamB} delivered a proper cricket story.`} 🏆`,
    ].join('\n');
  }, [awards?.manOfTheMatch, bestPartnership, finalScores, inningsStats, isMatchCompleted, match?.team1, match?.team2, mvp, resolvePlayerName, resultText, score?.toss, squads.team1?.name, squads.team2?.name, summary.toss, topBatter, topBowler]);

  const shareText = useMemo(() => {
    const url = getPublicMatchUrl();
    const teamA = match?.team1 || squads.team1?.name || 'Team A';
    const teamB = match?.team2 || squads.team2?.name || 'Team B';
    if (isMatchCompleted) {
      return [
        aiNarrativeSummary,
        '',
        'Public scorecard link:',
        url,
        '',
        'AI summary with @Pankaj score card',
        'Thank you bhai',
      ].join('\n');
    }

    return [
      '🏏 Live Match',
      '',
      `${teamA} vs ${teamB}`,
      '',
      `Current Score: ${formatPublicScoreLine({ runs: score.runs, wickets: score.wickets, balls: score.balls, overs: displayOvers })}`,
      `Overs: ${displayOvers}`,
      '',
      'Watch live:',
      url,
    ].join('\n');
  }, [aiNarrativeSummary, displayOvers, isMatchCompleted, match?.team1, match?.team2, score.balls, score.runs, score.wickets, squads.team1?.name, squads.team2?.name]);
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const handleShareSummary = useCallback(async () => {
    if (navigator.share) {
      await navigator.share({ text: shareText });
      return;
    }
    await navigator.clipboard?.writeText(shareText);
  }, [shareText]);

  // Celebration trigger for six/four/wicket
  useEffect(() => {
    if (!commentary.length) return;
    const latestEvent = commentary[0];
    const celebrationType = getCelebrationType(latestEvent);
    if (!celebrationType) return;

    const celebrationKey = `${latestEvent.over || ''}-${latestEvent.result || ''}-${latestEvent.isWicket || false}`;
    if (lastCelebrationKey.current === celebrationKey) return;

    lastCelebrationKey.current = celebrationKey;
    if (celebrationStorageKey) localStorage.setItem(celebrationStorageKey, celebrationKey);

    showCelebration({ type: celebrationType, ...celebrationCopy[celebrationType] });
  }, [commentary, showCelebration, celebrationStorageKey]);

  // Celebration for new bowler / new batter (after wicket)
  useEffect(() => {
    const currentStriker = currentStrikerId || '';
    const currentNonStriker = currentNonStrikerId || '';
    const currentBowlerId = getPlayerId(currentBowler) || score?.bowler || '';
    const previousPlayers = lastPlayersRef.current;
    const latestEvent = commentary?.[0];

    const currentWickets = Number(score?.wickets) || 0;
    const previousWickets = lastWicketsRef.current ?? currentWickets;

    if (currentWickets > previousWickets) pendingNewBatterRef.current = true;
    lastWicketsRef.current = currentWickets;

    if (!currentStriker && !currentNonStriker && !currentBowlerId) return;

    if (!previousPlayers.striker && !previousPlayers.nonStriker && !previousPlayers.bowler) {
      lastPlayersRef.current = { striker: currentStriker, nonStriker: currentNonStriker, bowler: currentBowlerId };
      return;
    }

    const latestEventType = getCelebrationType(latestEvent);
    if (latestEventType && !pendingNewBatterRef.current) return;

    if (
      previousPlayers.striker === currentStriker &&
      previousPlayers.nonStriker === currentNonStriker &&
      previousPlayers.bowler === currentBowlerId
    ) {
      return;
    }

    const strikerChanged = currentStriker && currentStriker !== previousPlayers.striker;
    const nonStrikerChanged = currentNonStriker && currentNonStriker !== previousPlayers.nonStriker;
    const isStrikeRotation =
      strikerChanged &&
      nonStrikerChanged &&
      currentStriker === previousPlayers.nonStriker &&
      currentNonStriker === previousPlayers.striker;

    const previousPair = [previousPlayers.striker, previousPlayers.nonStriker].filter(Boolean);
    const hasNewBatter = [currentStriker, currentNonStriker].some((p) => p && !previousPair.includes(p));

    const shouldDeferCelebration = celebration && ['six', 'four', 'wicket'].includes(celebration.type);
    const shouldDeferNewBatter =
      shouldDeferCelebration &&
      pendingNewBatterRef.current &&
      (strikerChanged || nonStrikerChanged) &&
      !isStrikeRotation &&
      hasNewBatter;

    if (!shouldDeferNewBatter) {
      lastPlayersRef.current = { striker: currentStriker, nonStriker: currentNonStriker, bowler: currentBowlerId };
    }

    if (shouldDeferCelebration) return;

    if (currentBowlerId && currentBowlerId !== previousPlayers.bowler) {
      const playerName = resolvePlayerName(currentBowlerId) || 'New bowler';
      showCelebration({
        type: 'bowler',
        title: celebrationCopy.bowler.title,
        subtitle: `${playerName} takes the ball.`,
      });
      setForceOverReset(true);
      return;
    }

    if ((strikerChanged || nonStrikerChanged) && pendingNewBatterRef.current && !isStrikeRotation && hasNewBatter) {
      const batterId = strikerChanged ? currentStriker : currentNonStriker;
      const playerName = resolvePlayerName(batterId) || 'New batter';
      showCelebration({
        type: 'batter',
        title: celebrationCopy.batter.title,
        subtitle: `${playerName} joins the middle.`,
      });
      pendingNewBatterRef.current = false;
    }
  }, [celebration, commentary, currentBowler, currentNonStrikerId, currentStrikerId, resolvePlayerName, score?.bowler, showCelebration]);

  useEffect(() => {
    const ballsInOver = (Number(score?.balls) || 0) % 6;
    if (ballsInOver > 0 && forceOverReset) {
      setForceOverReset(false);
    }
  }, [forceOverReset, score?.balls]);

  useEffect(() => {
    return () => {
      if (celebrationTimer.current) clearTimeout(celebrationTimer.current);
    };
  }, []);

  // --- Render helpers for tabs (keeps main JSX clean) ---
  const renderSummary = () => {
    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="pmc-card">
            <div className="pmc-card-head align-items-start">
              <div>
                <div className="pmc-card-title">{isMatchCompleted ? 'Final Match Summary' : 'Match Summary'}</div>
                <div className="pmc-muted small">{match?.team1 || 'Team A'} vs {match?.team2 || 'Team B'}</div>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <button type="button" className="btn btn-sm btn-primary" onClick={handleShareSummary}>
                  Share Summary
                </button>
                <a className="btn btn-sm btn-success" href={whatsappShareUrl} target="_blank" rel="noreferrer">WhatsApp</a>
              </div>
            </div>
            <div className="pmc-card-body">
              <div className="pmc-ai-summary-box mb-0">
                <div className="pmc-muted small text-uppercase fw-bold mb-2">AI-style narrative</div>
                <div className="pmc-ai-summary-text">{aiNarrativeSummary}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderScorecard = () => {
    if (inningsStats.length === 0) return <div className="alert alert-light mb-0">No innings data yet.</div>;

    return (
      <div id="scorecard-innings-accordion" className="accordion d-flex flex-column gap-3">
        {inningsStats.map((innings, index) => {
          const innSummary = innings.summary || {};
          const bat = derivePublicBattingRows({
            batters: innSummary.batting || [],
            score,
            events: scoreEvents,
            inningsNumber: innings.number,
            isMatchComplete,
          });
          const bowl = innSummary.bowling || [];
          const fow = innSummary.fallOfWickets || [];
          const partnerships = innSummary.partnerships || [];
          const scoreline = `${innings.runs || 0}-${innings.wickets || 0}`;
          const oversLabel = oversFromBalls(innings.balls);
          const inningsKey = getInningsKey(innings, index);
          const inningsId = inningsKey || innings.number;
          const headingId = `innings-heading-${inningsId || index}`;
          const collapseId = `innings-collapse-${inningsId || index}`;
          const isExpanded = openInningsKey === inningsKey;
          const inningsLabel = formatInningsLabel(innings.number);
          const teamShortName = innings.battingTeamShortName || innings.battingTeamShort || innings.battingTeam || 'Team';
          const accordionHeader = `${teamShortName} ${inningsLabel} — ${scoreline} (${oversLabel})`;

          return (
            <div key={`public-innings-${inningsKey}`} className="accordion-item pmc-card border rounded-4 bg-white shadow-sm">
              <h2 id={headingId} className="accordion-header">
                <button
                  className={`accordion-button${isExpanded ? '' : ' collapsed'} pmc-card-head`}
                  type="button"
                  aria-expanded={isExpanded}
                  aria-controls={collapseId}
                  onClick={() => toggleInningsAccordion(inningsKey)}
                >
                  <span className="pmc-card-title w-100 fs-4 text-start">{accordionHeader}</span>
                </button>
              </h2>

              <div
                id={collapseId}
                className={`accordion-collapse collapse${isExpanded ? ' show' : ''}`}
                aria-labelledby={headingId}
                role="region"
                hidden={!isExpanded}
              >
                <div className="pmc-card-head border-top-0 pt-0">
                  <div className="pmc-muted small">
                    {innings.bowlingTeam ? `vs ${innings.bowlingTeam}` : 'Bowling team TBD'}
                  </div>
                  <div className="text-end">
                    <div className="pmc-muted small">Run rate: {innSummary.runRate ?? 0}</div>
                  </div>
                </div>

                <div className="pmc-card-body">
                  <div className="row g-3">
                  <div className="col-12">
                    <h6 className="pmc-section-title">Batting</h6>
                    {bat.length ? (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle pmc-table">
                          <thead>
                          <tr>
                            <th>Batter</th>
                            <th className="text-end">R</th>
                            <th className="text-end">B</th>
                            <th className="text-end">4s</th>
                            <th className="text-end">6s</th>
                            <th className="text-end">SR</th>
                          </tr>
                          </thead>
                          <tbody>
                          {bat.map((batter, idx) => {
                            const batterRoleCodes = roleDirectory.get((batter.playerId || '').toString()) || [];
                            const batterName = renderPlayerNameWithRoles(getPublicRowPlayerName(batter, playerDirectory, 'Unknown player'), batter.participantKey || batter.playerId, batterRoleCodes);
                            const isNotOut = batter.derivedStatus === 'not-out';
                            const showNotOutStar = isNotOut;
                            const dismissal = formatDismissal(batter);
                            return (
                              <tr key={batter.playerId || `batter-${idx}`}>
                                <td className="fw-semibold">
                                  <div className="d-flex align-items-center gap-1">
                                    <span>
                                      {idx + 1}. {batterName}
                                    </span>
                                    {showNotOutStar ? (
                                      <span className="pmc-star" title="Not out" aria-label="Not out">
                                        ★
                                      </span>
                                    ) : null}
                                  </div>
                                  {dismissal ? <div className="pmc-muted small">{dismissal}</div> : null}
                                </td>
                                <td className="text-end">{batter.runs ?? 0}</td>
                                <td className="text-end">{batter.balls ?? 0}</td>
                                <td className="text-end">{batter.fours ?? 0}</td>
                                <td className="text-end">{batter.sixes ?? 0}</td>
                                <td className="text-end">{batter.strikeRate ?? '-'}</td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-light mb-0">No batting data yet.</div>
                    )}
                  </div>

                  <div className="col-12">
                    <h6 className="pmc-section-title">Bowling</h6>
                    {bowl.length ? (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle pmc-table">
                          <thead>
                          <tr>
                            <th>Bowler</th>
                            <th className="text-end">O</th>
                            <th className="text-end">M</th>
                            <th className="text-end">R</th>
                            <th className="text-end">W</th>
                            <th className="text-end">Econ</th>
                          </tr>
                          </thead>
                          <tbody>
                          {bowl.map((bowler, idx) => {
                            const bowlerName = getPublicRowPlayerName(bowler, playerDirectory, 'Unknown player');
                            const bowlerId = getPlayerId(bowler.playerId || bowler);
                            const isCurrentBowler = currentBowlerId && bowlerId && bowlerId === currentBowlerId;
                            return (
                              <tr key={bowler.playerId || `bowler-${idx}`}>
                                <td className="fw-semibold">
                                  {idx+1}. {bowlerName}
                                  {isCurrentBowler ? (
                                    <span className="pmc-star" title="Current bowler" aria-label="Current bowler">
                                      ★
                                    </span>
                                  ) : null}
                                </td>
                                <td className="text-end">{normalizeBowlingOvers(bowler.overs ?? 0)}</td>
                                <td className="text-end">{bowler.maidens ?? 0}</td>
                                <td className="text-end">{bowler.runsConceded ?? 0}</td>
                                <td className="text-end">{bowler.wickets ?? 0}</td>
                                <td className="text-end">{bowler.economy ?? '-'}</td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-light mb-0">No bowling data yet.</div>
                    )}
                  </div>

                  {fow.length ? (
                    <div className="col-12">
                      <h6 className="pmc-section-title">Fall of Wickets</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle pmc-table">
                          <thead>
                          <tr>
                            <th>Wkt</th>
                            <th>Score</th>
                            <th>Batter</th>
                            <th>Bowler</th>
                            <th>Overs</th>
                          </tr>
                          </thead>
                          <tbody>
                          {fow.map((entry, idx) => (
                            <tr key={`${entry.wicket}-${idx}`}>
                              <td>{entry.wicket}</td>
                              <td>
                                {entry.runs}-{entry.wicket}
                              </td>
                              <td className="fw-semibold">{resolvePlayerName(entry.batterId, 'Unknown player')}</td>
                              <td className="fw-semibold">{resolvePlayerName(entry.bowlerId, 'Unknown player')}</td>
                              <td>{entry.overs ?? '-'}</td>
                            </tr>
                          ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                    {partnerships.length ? (
                    <div className="col-12">
                      <h6 className="pmc-section-title">Partnerships</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle pmc-table">
                          <thead>
                          <tr>
                            <th>Wkt</th>
                            <th>Batters</th>
                            <th className="text-end">Runs (Balls)</th>
                            <th className="text-end">Overs</th>
                          </tr>
                          </thead>
                          <tbody>
                          {partnerships.map((partnership, idx) => {
                            const batter1 = partnership.batter1Name || resolvePlayerName(partnership.batter1 || partnership.batter1Id, 'Unknown player');
                            const batter2 = partnership.batter2Name || resolvePlayerName(partnership.batter2 || partnership.batter2Id, 'Unknown player');
                            const wicketLabel = partnership.wicket ? `${partnership.wicket}` : '—';
                            const oversLabel = partnership.startOver && partnership.endOver
                              ? `${partnership.startOver}-${partnership.endOver}`
                              : partnership.endOver || partnership.startOver || '-';
                            return (
                              <tr key={`${partnership.batter1 || 'bat'}-${partnership.batter2 || 'bat'}-${idx}`}>
                                <td>
                                  {wicketLabel}
                                  {partnership.isUnbroken ? (
                                    <span className="pmc-pill pmc-pill-muted ms-2">Unbroken</span>
                                  ) : null}
                                </td>
                                <td className="fw-semibold">
                                  {batter1} &amp; {batter2}
                                </td>
                                <td className="text-end">
                                  {partnership.runs ?? 0} ({partnership.balls ?? 0})
                                </td>
                                <td className="text-end">{oversLabel}</td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCommentary = () => {
    if (!commentary.length) return <div className="alert alert-light mb-0">No commentary yet.</div>;

    return (
      <div className="pmc-card">
        <div className="pmc-card-head">
          <div className="pmc-card-title">Commentary</div>
          <div className="pmc-muted small">Ball by ball ({commentary.length} deliveries)</div>
        </div>
        <div className="pmc-card-body">
          <div className="pmc-deliveries-scroll">
            <ul className="list-group list-group-flush">
            {commentary.map((event, index) => (
              <li key={`${event.over}-${index}`} className="list-group-item px-0 py-2 pmc-list-item">
                <div className="d-flex justify-content-between gap-2">
                  <div className="text-truncate">
                    <span className="fw-semibold">{formatOverLabel(event.over, event) || '-'}</span>{' '}
                    <span className="pmc-muted">
                      {event.bowler || 'Bowler'} to {event.striker || 'Batter'}
                    </span>
                    {event.detail ? <span className="pmc-muted"> • {formatExtraResult(event.detail)}</span> : null}
                  </div>
                  <span className={event.isWicket ? 'pmc-pill pmc-pill-danger' : 'pmc-pill pmc-pill-muted'}>
                    {formatExtraResult(event.result) || '-'}
                  </span>
                </div>
                <div className="mt-1">{event.commentary || event.text || `${event.bowler || 'Bowler'} to ${event.striker || 'Batter'}, ${formatExtraResult(event.result) || 'ball'}`}</div>
              </li>
            ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    return (
      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="pmc-card h-100">
            <div className="pmc-card-head">
              <div className="pmc-card-title">Top Batters</div>
            </div>
            <div className="pmc-card-body">
              {stats.topBatters?.length ? (
                <ul className="list-unstyled mb-0">
                  {stats.topBatters.map((b, index) => (
                    <li key={b.playerId} className="d-flex justify-content-between py-1">
                      <span className="fw-semibold">{index+1}. {getPublicRowPlayerName(b, playerDirectory, 'Unknown player')}</span>
                      <span className="pmc-muted">
                        {b.runs} ({b.balls})
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="pmc-muted">Awaiting batting stats.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="pmc-card h-100">
            <div className="pmc-card-head">
              <div className="pmc-card-title">Top Bowlers</div>
            </div>
            <div className="pmc-card-body">
              {stats.topBowlers?.length ? (
                <ul className="list-unstyled mb-0">
                  {stats.topBowlers.map((b, index) => (
                    <li key={b.playerId} className="d-flex justify-content-between py-1">
                      <span className="fw-semibold">{index+1}. {getPublicRowPlayerName(b, playerDirectory, 'Unknown player')}</span>
                      <span className="pmc-muted">
                        {b.wickets} wkts • {normalizeBowlingOvers(b.overs)} ov
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="pmc-muted">Awaiting bowling stats.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="pmc-card">
            <div className="pmc-card-head">
              <div className="pmc-card-title">Extras</div>
            </div>
            <div className="pmc-card-body">
              <div className="pmc-muted">
                Wides <strong>{(scoreboardSummary?.extras || stats.extras)?.wides || 0}</strong> • No balls <strong>{(scoreboardSummary?.extras || stats.extras)?.noBalls || 0}</strong>{' '}
                • Byes <strong>{(scoreboardSummary?.extras || stats.extras)?.byes || 0}</strong> • Leg byes{' '}
                <strong>{(scoreboardSummary?.extras || stats.extras)?.legByes || 0}</strong> • Penalty <strong>{(scoreboardSummary?.extras || stats.extras)?.penalty || 0}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSquads = () => (
    <div className="row g-3">
      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">{squads.team1?.name || match?.team1 || 'Team 1'}</div>
          </div>
          <div className="pmc-card-body">
            {squads.team1?.players?.length ? (
              <ul className="list-unstyled mb-0">
                {squads.team1.players.map((p, index) => (
                  <li key={p.id} className="py-1 fw-semibold">
                    {index+1}. {renderPlayerNameWithRoles({ name: p.name }, p.id, extractRoleCodes(p.roles))}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="pmc-muted">Lineup pending.</div>
            )}
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">{squads.team2?.name || match?.team2 || 'Team 2'}</div>
          </div>
          <div className="pmc-card-body">
            {squads.team2?.players?.length ? (
              <ul className="list-unstyled mb-0">
                {squads.team2.players.map((p, index) => (
                  <li key={p.id} className="py-1 fw-semibold">
                    {index+1}. {renderPlayerNameWithRoles({ name: p.name }, p.id, extractRoleCodes(p.roles))}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="pmc-muted">Lineup pending.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAwards = () => (
    <div className="row g-3">
      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">{isMatchComplete ? 'Man of the Match' : 'MVP'}</div>
          </div>
          <div className="pmc-card-body">
            {(awards?.manOfTheMatch || mvp) ? (
              <div>
                <div className="fw-bold">
                  {awards?.manOfTheMatch?.name ||
                    resolvePlayerName(awards?.manOfTheMatch?.playerId) ||
                    mvp?.name ||
                    resolvePlayerName(mvp?.playerId) ||
                    'Player'}
                </div>
                <div className="pmc-muted">
                  Runs: {awards?.manOfTheMatch?.runs || mvp?.runs || 0} • Wickets:{' '}
                  {awards?.manOfTheMatch?.wickets || mvp?.wickets || 0}
                </div>
              </div>
            ) : (
              <div className="pmc-muted">
                {isMatchComplete
                  ? 'Man of the match will appear after the final ball.'
                  : 'MVP will update after key performances.'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">Match Awards</div>
          </div>
          <div className="pmc-card-body">
            {awards ? (
              <div className="d-flex flex-column gap-2">
                <div>
                  <div className="pmc-muted small">Best batsman</div>
                  <div className="fw-bold">
                    {getPublicRowPlayerName(awards.bestBatsman, playerDirectory, 'TBD')}
                  </div>
                </div>
                <div>
                  <div className="pmc-muted small">Best bowler</div>
                  <div className="fw-bold">
                    {getPublicRowPlayerName(awards.bestBowler, playerDirectory, 'TBD')}
                  </div>
                </div>
                <div>
                  <div className="pmc-muted small">Best fielder</div>
                  <div className="fw-bold">
                    {getPublicRowPlayerName(awards.bestFielder, playerDirectory, 'TBD')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="pmc-muted">Match awards will appear once the innings are complete.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderInfo = () => (
    <div className="row g-3">
      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">Venue & Schedule</div>
          </div>
          <div className="pmc-card-body">
            <div className="d-flex flex-column gap-2">
              <div>
                <div className="pmc-muted small">Venue</div>
                <div className="fw-semibold">{info.venue || 'TBD'}</div>
              </div>
              <div>
                <div className="pmc-muted small">Date & Time</div>
                <div className="fw-semibold">
                  {info.date || 'TBD'} {info.time ? `• ${info.time}` : ''}
                </div>
              </div>
              {summary.toss ? (
                <div>
                  <div className="pmc-muted small">Toss</div>
                  <div className="fw-semibold">{summary.toss}</div>
                </div>
              ) : null}
              {resultText ? (
                <div>
                  <div className="pmc-muted small">Result</div>
                  <div className="fw-semibold">{resultText}</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="pmc-card h-100">
          <div className="pmc-card-head">
            <div className="pmc-card-title">Officials</div>
          </div>
          <div className="pmc-card-body">
            <div className="pmc-muted">
              {info.officials?.umpire1 || 'Umpire 1'}
              {info.officials?.umpire2 ? ` • ${info.officials.umpire2}` : ''}
              {info.officials?.matchReferee ? ` • Referee: ${info.officials.matchReferee}` : ''}
            </div>
          </div>
        </div>
      </div>

      {analysis?.highlights?.length ? (
        <div className="col-12">
          <div className="pmc-card">
            <div className="pmc-card-head">
              <div className="pmc-card-title">Analysis</div>
            </div>
            <div className="pmc-card-body">
              <ul className="mb-0">
                {analysis.highlights.map((h, i) => (
                  <li key={`${h}-${i}`}>{h}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  // --- Main UI ---
  return (
    <div className="pmc-wrap">
      <style>{`
        :root{
          --pmc-bg1:#0e5a26;
          --pmc-bg2:#1f7a34;
          --pmc-ink:#0b1220;
          --pmc-white:rgba(255,255,255,.92);
          --pmc-white2:rgba(255,255,255,.78);
          --pmc-border:rgba(255,255,255,.18);
          --pmc-shadow:0 14px 40px rgba(0,0,0,.20);
          --pmc-accent:#22c55e;     /* boundaries */
          --pmc-danger:#ff2d55;     /* wickets/extras */
          --pmc-gold:#fbbf24;       /* running/other */
        }
        .pmc-wrap{
          min-height: 100vh;
        }



        .pmc-container{ margin: 0 auto; }

        .pmc-glass{
          border: 1px solid rgba(255,255,255,.25);
          border-radius: 20px;
          box-shadow: var(--pmc-shadow);
          backdrop-filter: blur(10px);
        }


        .pmc-hero{ padding: 18px 18px 14px; }
        .pmc-hero-title{
          font-weight: 900;
          letter-spacing: -0.02em;
          font-size: 40px;
          color: rgba(10,20,25,.92);
          margin: 6px 0 2px;
          text-transform: uppercase;
        }
        .pmc-hero-sub{ color: rgba(10,20,25,.70); }

        .pmc-pills{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
        .pmc-pill{
          display:inline-flex; align-items:center; gap:8px;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          background: rgba(255,255,255,.72);
          border: 1px solid rgba(0,0,0,.08);
          color: rgba(10,20,25,.78);
        }
        .pmc-pill-live{
          background: rgba(255,45,85,.14);
          border-color: rgba(255,45,85,.22);
          color: rgba(190,20,55,.92);
          letter-spacing: .10em;
        }
        .pmc-pill-muted{
          background: rgba(255,255,255,.55);
          color: rgba(10,20,25,.70);
        }
        .pmc-pill-danger{
          background: rgba(255,45,85,.12);
          border-color: rgba(255,45,85,.22);
          color: rgba(190,20,55,.95);
        }

        .pmc-actions{ display:flex; flex-wrap:wrap; gap:10px; justify-content:flex-end; }
        .pmc-actions .btn{ border-radius: 12px; font-weight: 700; }
        .pmc-actions .btn.btn-sm{ padding: 8px 12px; }

 

.pmc-scorebar{
         background:
    linear-gradient(
      90deg,
      rgba(34,197,94,.18),
      rgba(59,130,246,.15),
      rgba(168,85,247,.15)
    ),
    rgba(255,255,255,.92);
  border: 1px solid rgba(0,0,0,.08);
  box-shadow: 0 16px 34px rgba(0,0,0,.20);
  margin-top: 14px;
  padding: 14px 16px;
}

.pmc-scorebar[data-live="true"]{
  box-shadow:
    0 0 0 2px rgba(255,45,85,.25),
    0 18px 40px rgba(0,0,0,.35);
}


        .pmc-score-big{
          font-size: 34px;
          font-weight: 900;
          letter-spacing: -0.02em;
          color: rgba(10,20,25,.92);
          line-height: 1.1;
        }
        .pmc-muted{ color: rgba(10,20,25,.64); }
        .pmc-kv{ display:flex; gap:14px; flex-wrap:wrap; align-items:center; }
        .pmc-kv strong{ color: rgba(10,20,25,.92); }
        .pmc-chase-stack{
          display:flex;
          flex-direction:column;
          gap: 12px;
          width: 100%;
          margin-top: 10px;
        }
        .pmc-chase-summary{
          display:grid;
          grid-template-columns: repeat(3, minmax(110px, 1fr));
          gap: 10px;
          width: 100%;
        }
        .pmc-chase-stat{
          border: 1px solid rgba(34,197,94,.22);
          border-radius: 16px;
          padding: 10px 12px;
          background: linear-gradient(180deg, rgba(34,197,94,.14), rgba(255,255,255,.94));
          box-shadow: inset 0 1px 0 rgba(255,255,255,.65);
        }
        .pmc-chase-label{
          display:block;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: rgba(10,20,25,.60);
          margin-bottom: 4px;
        }
        .pmc-chase-value{
          font-size: 26px;
          font-weight: 900;
          letter-spacing: -0.02em;
          color: rgba(10,20,25,.94);
          line-height: 1.1;
        }
        .pmc-winprob{
          border: 1px solid rgba(59,130,246,.16);
          border-radius: 18px;
          padding: 12px 14px;
          background: linear-gradient(180deg, rgba(59,130,246,.08), rgba(255,255,255,.95));
          box-shadow: inset 0 1px 0 rgba(255,255,255,.78);
        }
        .pmc-winprob-head{
          display:grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          gap: 12px;
          align-items:center;
          margin-bottom: 10px;
        }
        .pmc-winprob-team{
          display:flex;
          align-items:center;
          gap: 8px;
          min-width: 0;
          font-size: 14px;
          color: rgba(10,20,25,.82);
        }
        .pmc-winprob-team strong{
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .pmc-winprob-team-away{
          justify-content:flex-end;
        }
        .pmc-winprob-label{
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: rgba(10,20,25,.52);
        }
        .pmc-winprob-bar{
          display:flex;
          overflow:hidden;
          width: 100%;
          height: 14px;
          border-radius: 999px;
          background: rgba(148,163,184,.22);
          border: 1px solid rgba(148,163,184,.24);
        }
        .pmc-winprob-fill{
          height: 100%;
          transition: width .35s ease;
        }
        .pmc-winprob-fill-batting{
          background: linear-gradient(90deg, rgba(34,197,94,.92), rgba(59,130,246,.82));
        }
        .pmc-winprob-fill-bowling{
          background: linear-gradient(90deg, rgba(251,191,36,.85), rgba(239,68,68,.88));
        }
        @media (max-width: 768px){
          .pmc-chase-summary{
            grid-template-columns: 1fr;
          }
          .pmc-chase-value{
            font-size: 22px;
          }
          .pmc-winprob-head{
            grid-template-columns: 1fr;
            justify-items:start;
          }
          .pmc-winprob-team-away{
            justify-content:flex-start;
          }
        }

        .pmc-sticky{
          position: sticky;
          top: 12px;
          z-index: 10;
        }

        .pmc-tabs{
          margin-top: 14px;
          display:flex;
          gap:10px;
          flex-wrap:wrap;
        }
        .pmc-tab{
          border: 1px solid rgba(255,255,255,.25);
          background: rgba(255,255,255,.35);
          color: rgba(10,20,25,.78);
          font-weight: 800;
          border-radius: 14px;
          padding: 9px 14px;
          cursor: pointer;
          transition: .15s ease;
          user-select:none;
        }
        .pmc-tab:hover{ transform: translateY(-1px); background: rgba(255,255,255,.45); }
        .pmc-tab.active{
          background: rgba(255,255,255,.82);
          border-color: rgba(0,0,0,.08);
          color: rgba(10,20,25,.92);
          box-shadow: 0 10px 22px rgba(0,0,0,.14);
        }

        .pmc-grid{ display:grid; grid-template-columns: minmax(0,1fr) minmax(0,3fr); gap: 16px; margin-top: 16px; }
        .pmc-grid-single{ grid-template-columns: 1fr; }
        @media (max-width: 992px){ .pmc-grid{ grid-template-columns: 1fr; } .pmc-hero-title { font-size: 20px;} }

        .pmc-card{
          background: rgba(255,255,255,.92);
          border: 1px solid rgba(0,0,0,.06);
          border-radius: 18px;
          box-shadow: 0 18px 38px rgba(0,0,0,.18);
          overflow:hidden;
        }
        .pmc-card-head{
          display:flex; align-items:flex-start; justify-content:space-between;
          padding: 14px 16px;
          background: rgba(255,255,255,.88);
          border-bottom: 1px solid rgba(0,0,0,.06);
          gap:12px;
        }
        .pmc-card-title{
          font-weight: 900;
          letter-spacing: .06em;
          font-size: 12px;
          text-transform: uppercase;
          color: rgba(10,20,25,.78);
        }
        .ca-accordion-chevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          line-height: 1;
          color: rgba(10,20,25,.72);
          transition: transform .15s ease;
          margin-left: 8px;
        }
        .ca-accordion-chevron--open { transform: rotate(180deg); }
        .pmc-card-body{ padding: 14px 16px; }
        .pmc-ai-summary-box{
          border:1px solid rgba(16,185,129,.22);
          border-radius:18px;
          padding:16px;
          background:linear-gradient(135deg, rgba(16,185,129,.10), rgba(59,130,246,.08));
        }
        .pmc-ai-summary-text{
          white-space:pre-wrap;
          line-height:1.65;
          font-weight:600;
          color:#0f172a;
        }
        .pmc-card-body .list-group-item {
          background-color: transparent;
        }
        .pmc-scoreline{ font-weight: 900; color: rgba(10,20,25,.92); }
        .pmc-section-title{
          font-weight: 900;
          font-size: 12px;
          letter-spacing: .06em;
          text-transform: uppercase;
          color: rgba(10,20,25,.70);
          margin-bottom: 10px;
        }

        .pmc-onfield-row{ display:flex; justify-content:space-between; gap:10px; padding: 10px 0; }
        .pmc-divider{ border-top:1px solid rgba(0,0,0,.07); margin: 10px 0; }
        .pmc-tag{
          font-size: 11px;
          font-weight: 900;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(34,197,94,.12);
          color: rgba(12,120,50,.92);
          border: 1px solid rgba(34,197,94,.18);
          white-space: nowrap;
          height: 100%;
        }
        .pmc-tag-muted{
          background: rgba(0,0,0,.05);
          color: rgba(10,20,25,.72);
          border-color: rgba(0,0,0,.06);
        }

        .pmc-visuals-grid{
          display:grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 992px){ .pmc-visuals-grid{ grid-template-columns: 1fr; } }

        .pmc-engagement-grid{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
        .pmc-engagement-card{ background:#fff; border-radius:20px; border:1px solid rgba(15,23,42,.08); padding:18px; min-height:112px; }
        .pmc-engagement-card--wide{ grid-column:1/-1; }
        @media (max-width: 768px){ .pmc-engagement-grid{ grid-template-columns:1fr; } }

        .pmc-mini{
          border: 1px solid rgba(0,0,0,.06);
          border-radius: 16px;
          padding: 12px 12px;
          background: rgba(255,255,255,.95);
        }

        .pmc-donut{
          width: 86px; height: 86px; border-radius: 999px;
          display:grid; place-items:center;
        }
        .pmc-donut-center{
          width: 56px; height: 56px; border-radius: 999px;
          background: rgba(255,255,255,.95);
          border: 1px solid rgba(0,0,0,.08);
          display:grid; place-items:center;
          font-weight: 900;
          color: rgba(10,20,25,.92);
        }

        .pmc-compbar{
          height: 10px;
          border-radius: 999px;
          overflow:hidden;
          background: rgba(0,0,0,.06);
          display:flex;
          margin-top: 10px;
        }
        .pmc-compbar > span{ height:100%; display:block; }
        .pmc-bounds{ background: var(--pmc-accent); }
        .pmc-extras{ background: var(--pmc-danger); }
        .pmc-other{ background: var(--pmc-gold); }

        .pmc-wicket-ring{
          width: 92px; height: 92px; border-radius: 999px;
          display:grid; place-items:center;
          margin: 8px 0 6px;
        }
        .pmc-wicket-center{
          width: 62px; height: 62px; border-radius: 999px;
          background: rgba(255,255,255,.95);
          border: 1px solid rgba(0,0,0,.08);
          display:grid; place-items:center;
          font-weight: 900;
        }

        .pmc-bars{ height: 10px; background: rgba(0,0,0,.06); border-radius: 999px; overflow:hidden; }
        .pmc-bars > div{ height: 100%; background: rgba(34,197,94,.85); border-radius: 999px; }

        .pmc-ball-row{
          display:flex; flex-wrap:wrap; gap:10px;
          margin-top: 10px;
        }
        .pmc-ball{
          width: 58px; height: 58px;
          border-radius: 18px;
          border: 1px solid rgba(0,0,0,.08);
          background: rgba(0,0,0,.04);
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          font-weight: 900;
          color: rgba(10,20,25,.85);
          line-height: 1;
          text-align:center;
        }
        .pmc-ball-label{
          font-size: 18px;
          font-weight: 900;
          text-transform: uppercase;
        }
        .pmc-ball-over{
          font-size: 12px;
          font-weight: 700;
          opacity: .7;
          margin-top: 4px;
        }
        .pmc-ball.dot{ background: rgba(0,0,0,.04); }
        .pmc-ball.run{ background: rgba(34,197,94,.12); border-color: rgba(34,197,94,.22); }
        .pmc-ball.four{ background: rgba(59,130,246,.14); border-color: rgba(59,130,246,.24); }
        .pmc-ball.six{ background: rgba(168,85,247,.14); border-color: rgba(168,85,247,.24); }
        .pmc-ball.extra{ background: rgba(251,191,36,.18); border-color: rgba(251,191,36,.25); }
        .pmc-ball.w{ background: rgba(255,45,85,.14); border-color: rgba(255,45,85,.25); }
        .pmc-ball.jackpot{
          background: linear-gradient(145deg, rgba(245,197,66,.32), rgba(255,255,255,.92));
          border-color: rgba(245,197,66,.72);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.85), 0 8px 20px rgba(245,197,66,.22);
        }
        .pmc-jackpot-banner{
          display:flex; align-items:center; gap:10px; width:100%; margin: 0 0 12px; padding: 10px 12px;
          border-radius: 16px; border: 1px solid rgba(245,197,66,.72);
          background: linear-gradient(135deg, rgba(245,197,66,.24), rgba(255,255,255,.94));
          box-shadow: 0 10px 24px rgba(245,197,66,.18); color: rgba(10,20,25,.88);
          font-size: 12px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase;
        }
        .pmc-jackpot-badge{
          display:inline-grid; place-items:center; width: 20px; height: 20px; margin-left: 8px;
          border-radius: 999px; border: 1px solid rgba(168,116,0,.55);
          background: radial-gradient(circle at 35% 30%, #fff7cf 0, #F5C542 58%, #d89c10 100%);
          color: #10284A; box-shadow: inset 0 1px 0 rgba(255,255,255,.75), 0 4px 12px rgba(245,197,66,.30);
          font-size: 11px; font-weight: 900; line-height: 1; vertical-align: middle;
        }

        .pmc-table thead th{
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: rgba(10,20,25,.60);
          border-bottom: 1px solid rgba(0,0,0,.08);
        }
        .pmc-table tbody td{
          border-bottom: 1px solid rgba(0,0,0,.06);
        }
        .pmc-star{
          color: var(--pmc-gold);
          font-size: 0.95em;
          margin-left: 6px;
        }
        .pmc-list-item{ border-color: rgba(0,0,0,.06) !important; }
        .pmc-deliveries-scroll{
          max-height: 420px;
          overflow-y: auto;
          padding-right: 4px;
        }
        @media (max-width: 768px){
          .pmc-deliveries-scroll{ max-height: 360px; }
        }
      `}</style>

      <div className="pmc-container">
        {/* Celebration */}
        {celebration ? (
          <div className={`celebration-popup celebration-${celebration.type}`} role="status" aria-live="polite">
            <div className="celebration-card">
              <div className="celebration-icon" aria-hidden="true">
                {celebrationIcons[celebration.type] || '✨'}
              </div>
              <div>
                <p className="celebration-title">{celebration.title}</p>
                <p className="celebration-subtitle">{celebration.subtitle}</p>
              </div>
            </div>
            <div className="celebration-confetti" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : null}

        {/* HERO */}
        <div className="pmc-glass pmc-hero pmc-card">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
            <div>
              <div className="pmc-pills mb-2">
                <span className="pmc-pill">Public match center</span>
                <span className="pmc-pill pmc-pill-muted">{tournament?.name || 'Tournament'}</span>
                <span className={`pmc-pill ${summary.status === 'in_progress' ? 'pmc-pill-live' : 'pmc-pill-muted'}`}>
                  {statusLabel(summary.status)}
                </span>
                {info.date || info.time ? (
                  <span className="pmc-pill pmc-pill-muted">
                    {info.date || 'Date'} {info.time ? `• ${info.time}` : ''}
                  </span>
                ) : null}
              </div>

              <div className="pmc-hero-title">
                {match?.team1 || 'Team 1'} vs {match?.team2 || 'Team 2'}
              </div>

              <div className="pmc-hero-sub">
                {info.venue || 'Venue TBD'}
                {resultText ? ` • ${resultText}` : ''}
              </div>
            </div>

            <div className="pmc-actions">
              {watchUrl || dynamicWatchRedirectUrl ? (
                <a className="btn btn-primary btn-sm" href={dynamicWatchRedirectUrl} target="_blank" rel="noreferrer">
                  Watch live
                </a>
              ) : (
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  Stream not available
                </button>
              )}

              {tournamentId ? (
                <Link
                  className="btn btn-outline-primary btn-sm d-flex align-items-center"
                  to={`/public/tournaments/${tournamentId}/leaderboard`}
                  state={{ matchId }}
                >
                  Leaderboard
                </Link>
              ) : null}

              <button className="btn btn-dark btn-sm" onClick={() => loadPublicView({ withSpinner: true })} disabled={isLoading}>
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>


          {/* Sticky Score Bar */}
          <div className="pmc-glass pmc-scorebar">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex flex-column flex-grow-1">
                  <div className="pmc-score-big">
                    {summary.scoreLine || `${score.runs || 0}-${score.wickets || 0}`}
                  </div>
                  {chaseSummary.show ? (
                    <div className="pmc-chase-stack">
                      <div className="pmc-chase-summary" aria-label="Chase summary">
                        <div className="pmc-chase-stat">
                          <span className="pmc-chase-label">Runs needed</span>
                          <span className="pmc-chase-value">{chaseSummary.runsNeeded}</span>
                        </div>
                        <div className="pmc-chase-stat">
                          <span className="pmc-chase-label">Balls left</span>
                          <span className="pmc-chase-value">{chaseSummary.ballsLeft}</span>
                        </div>
                        <div className="pmc-chase-stat">
                          <span className="pmc-chase-label">Required RR</span>
                          <span className="pmc-chase-value">{chaseSummary.requiredRunRate}</span>
                        </div>
                      </div>
                      {winProbability.show ? (
                        <div className="pmc-winprob" aria-label="Win probability">
                          <div className="pmc-winprob-head">
                            <span className="pmc-winprob-team">
                              <strong>{winProbability.battingTeam}</strong>
                              <span>{winProbability.battingTeamProbability}%</span>
                            </span>
                            <span className="pmc-winprob-label">Win Probability</span>
                            <span className="pmc-winprob-team pmc-winprob-team-away">
                              <span>{winProbability.bowlingTeamProbability}%</span>
                              <strong>{winProbability.bowlingTeam}</strong>
                            </span>
                          </div>
                          <div className="pmc-winprob-bar" role="img" aria-label={`${winProbability.battingTeam} ${winProbability.battingTeamProbability} percent, ${winProbability.bowlingTeam} ${winProbability.bowlingTeamProbability} percent`}>
                            <span
                              className="pmc-winprob-fill pmc-winprob-fill-batting"
                              style={{ width: `${winProbability.battingTeamProbability}%` }}
                            />
                            <span
                              className="pmc-winprob-fill pmc-winprob-fill-bowling"
                              style={{ width: `${winProbability.bowlingTeamProbability}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {dlsProjection?.pending ? (
                    <div className="alert alert-warning py-2 mb-0" data-testid="dls-score-banner">
                      <strong>DLS</strong> Match interrupted — revision pending
                    </div>
                  ) : dlsProjection?.active ? (
                    <div className="alert alert-info py-2 mb-0" data-testid="dls-score-banner">
                      <strong>DLS</strong>{' '}
                      <span data-testid="dls-target">{dlsProjection.revisedTarget ? `Target ${dlsProjection.revisedTarget} (DLS)` : 'Overs revised (DLS)'}</span>
                      {dlsProjection.revisedTarget ? <>{' · '}<span data-testid="dls-runs-required">Need {dlsProjection.runsRequired}</span>{' from '}<span data-testid="dls-balls-remaining">{dlsProjection.ballsRemaining} balls</span></> : null}
                      {dlsProjection.requiredRunRate !== null ? <> · <span data-testid="dls-required-run-rate">Req RR {Number(dlsProjection.requiredRunRate).toFixed(2)}</span></> : null}
                      {dlsProjection.parScore !== null ? <> · <span data-testid="dls-par-score">Par {dlsProjection.parScore}</span></> : null}
                    </div>
                  ) : null}
                  {chaseComparison?.applicable ? (
                    <div className="border rounded-3 bg-white p-2 mt-2" data-testid="public-chase-comparison">
                      <div className="d-flex justify-content-between text-uppercase small fw-semibold text-secondary">
                        <span>Chase Comparison</span><span>{chaseComparison.overs} overs</span>
                      </div>
                      <div className="d-flex justify-content-between mt-1">
                        <span><small className="d-block text-secondary text-uppercase">Current</small><strong>{chaseComparison.current.runs}/{chaseComparison.current.wickets}</strong></span>
                        <span className="text-end"><small className="d-block text-secondary text-uppercase">{chaseComparison.firstInningsFinal ? '1st Innings Final' : '1st Innings'}</small><strong>{chaseComparison.firstInnings.runs}/{chaseComparison.firstInnings.wickets}</strong></span>
                      </div>
                      <div className="small fw-semibold text-primary text-uppercase mt-1">{chaseComparison.statusText}</div>
                      {chaseComparison.firstInningsFinal ? <div className="small text-secondary">1st innings ended at {chaseComparison.firstInningsEndedAt} overs</div> : null}
                    </div>
                  ) : null}
                </div>
                <div className="pmc-kv">
                  <span className="pmc-muted">
                    Overs: <strong>{displayOvers} ({totalOver})</strong>
                    {isJackpotOverActive ? (
                      <span className="pmc-jackpot-badge" aria-hidden="true">J</span>
                    ) : null}
                  </span>
                  {isInningsBreak ? (
                    <span className="pmc-pill pmc-pill-muted">Innings break</span>
                  ) : null}
                  {showTarget ? (
                    <span className="pmc-muted">
                      Target: <strong>{targetRuns}</strong>
                    </span>
                  ) : null}
                  {!chaseSummary.show ? (
                    <span className="pmc-muted">
                      RR: <strong>{summary.runRate ?? 0}</strong>
                    </span>
                  ) : null}
                  {Number.isFinite(projectedScore) ? (
                    <span className="pmc-muted">
                      Projected score: <strong>{projectedScore}</strong>
                    </span>
                  ) : null}
                  {hasPowerPlay ? (
                    <span className="pmc-muted">
                      Power plays: <strong>{formatPowerPlaySummary(powerPlays)}</strong>
                      {isPowerPlayOver ? (
                        <span className="pmc-pill pmc-pill-live ms-2">Live</span>
                      ) : null}
                    </span>
                  ) : null}
                  <span className="pmc-muted">
                    Batting: <strong>{summary.battingTeam || score.battingTeam || match?.team1 || 'TBD'}</strong>
                  </span>
                  <span className="pmc-muted">
                    Bowling: <strong>{summary.bowlingTeam || score.bowlingTeam || match?.team2 || 'TBD'}</strong>
                  </span>
                </div>
              </div>

              <div className="pmc-muted small d-flex flex-column align-items-md-end gap-1">
                <span>
                  Online now: <strong>{onlineCount !== null ? onlineCount : '—'}</strong>
                </span>
                <span>
                  Auto-refresh every 15s{lastUpdatedAt ? ` • Updated ${timeAgo(lastUpdatedAt)}` : ''}
                </span>
                {onlineError ? <span className="text-warning">{onlineError}</span> : null}
              </div>
            </div>

</div>

          {/* Tabs */}
          <div className="pmc-tabs">
            {[
              ['live', 'Live'],
              ['quick-visuals', 'Quick Visuals'],
              ['scorecard', 'Scorecard'],
              ['commentary', 'Commentary'],
              ['stats', 'Stats'],
              ['summary', 'Summary'],
              ['squads', 'Squads'],
              ['awards', 'Awards'],
              ['info', 'Info'],
            ].map(([key, label]) => (
              <div
                key={key}
                className={`pmc-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' ? setActiveTab(key) : null)}
              >
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Errors / Loading */}
        {(error || (!match && !isLoading)) && (
          <div className="alert alert-danger mt-3" role="alert">
            {error || 'Public match view is unavailable.'}
          </div>
        )}
        {isLoading && (
          <div className="alert alert-secondary mt-3" role="alert">
            Loading public match view...
          </div>
        )}

        {/* Content */}
        {match ? (
          <div className="mt-3">
            {activeTab === 'live' ? (
              <div className="pmc-grid">
                {/* On Field */}
                <div className="pmc-card">
                  <div className="pmc-card-head">
                    <div className="pmc-card-title">On Field</div>
                    <div className="pmc-muted small">
                      {summary.status === 'in_progress' ? 'Live players' : 'Lineup'}
                    </div>
                  </div>

                  <div className="pmc-card-body">
                    <div className="pmc-onfield-row">
                      <div>
                        <div className="fw-bold">
                          {renderPlayerNameWithRoles(currentBatter, currentStrikerId, roleDirectory.get(`${currentStrikerId || ''}`) || []) || 'Awaiting striker'}
                        </div>
                        <div className="pmc-muted">
                          {currentBatter
                            ? `${currentBatter.runs} (${currentBatter.balls}) • SR ${currentBatter?.strikeRate ?? '-'}`
                            : 'No stats yet'}
                        </div>
                      </div>
                      <span className="pmc-tag">Striker</span>
                    </div>

                    <div className="pmc-onfield-row">
                      <div>
                        <div className="fw-bold">
                          {renderPlayerNameWithRoles(currentNonStriker, currentNonStrikerId, roleDirectory.get(`${currentNonStrikerId || ''}`) || []) || 'Awaiting non-striker'}
                        </div>
                        <div className="pmc-muted">
                          {currentNonStriker
                            ? `${currentNonStriker.runs} (${currentNonStriker.balls}) • SR ${
                              currentNonStriker?.strikeRate ?? '-'
                            }`
                            : 'No stats yet'}
                        </div>
                      </div>
                      <span className="pmc-tag pmc-tag-muted">Non-striker</span>
                    </div>

                    {currentPartnership ? (
                      <div className="pmc-muted small mt-3">
                        <strong>Partnership:</strong> {currentPartnership.runs} runs ({currentPartnership.balls} balls)
                      </div>
                    ) : null}

                    <div className="pmc-divider" />

                    <div className="pmc-onfield-row">
                      <div>
                        <div className="fw-bold">
                          {renderPlayerNameWithRoles(currentBowler, currentBowlerId || score.bowler, roleDirectory.get(`${currentBowlerId || score.bowler || ''}`) || []) || 'Awaiting bowler'}
                          {isJackpotOverActive ? (
                            <span className="pmc-jackpot-badge" title="Jackpot over active" aria-label="Jackpot over active">J</span>
                          ) : null}
                        </div>
                        <div className="pmc-muted">
                          {currentBowler
                            ? `${currentBowler.wickets ?? 0}W • ${normalizeBowlingOvers(currentBowler.overs ?? 0)} ov • Eco ${
                              currentBowler?.economy ?? '-'
                            }`
                            : 'No stats yet'}
                        </div>
                        <div className="pmc-muted small">Runs: {currentBowler?.runsConceded ?? 0}</div>
                        <div className="pmc-muted small">
                          Current over: {currentOverSummary.ballsInOver}/6 balls • Runs: {currentOverSummary.runsInOver}
                        </div>
                        {lastCompletedOver ? (
                          <div className="pmc-muted small mt-2">
                            Last over: <strong>{lastCompletedOver.runs} runs</strong> ({lastCompletedOver.labels.join(', ')})
                          </div>
                        ) : null}
                      </div>
                      <span className="pmc-tag pmc-tag-muted">Bowler</span>
                    </div>

                    <div className="pmc-divider" />

                    <div className="pmc-muted small mb-2">Current over balls</div>
                    <div className="pmc-ball-row">
                      {currentOverDeliveries.length ? (
                        currentOverDeliveries.map((event, index) => {
                          const type = ballChipType(event);
                          const label = compactBallLabel(event);
                          const overLabel = formatOverLabel(event.over, event);
                          const chipKey = event?._id || event?.id || `${overLabel}-${index}`;
                          const jackpotClass = isJackpotDelivery(event) ? ' jackpot' : '';
                          return (
                            <div key={chipKey} className={`pmc-ball ${type}${jackpotClass}`}>
                              <span className="pmc-ball-label">{label}</span>
                              <span className="pmc-ball-over">{overLabel}</span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="pmc-muted">No balls yet in this over.</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Viewer Engagement */}
                <ViewerEngagementPanel
                  engagement={engagement}
                  onlineCount={onlineCount}
                  engagementNotice={engagementNotice}
                  onReaction={handleReaction}
                  onPollVote={handlePollVote}
                  onFollow={handleFollow}
                  onChatSubmit={handleChatSubmit}
                  chatText={chatText}
                  setChatText={setChatText}
                  activeSponsorCard={activeSponsorCard}
                  pendingReaction={pendingReaction}
                  pendingPollOption={pendingPollOption}
                  pendingFollow={pendingFollow}
                  pendingChat={pendingChat}
                />
              </div>
            ) : null}


            {activeTab === 'quick-visuals' ? (
              <div className="pmc-grid pmc-grid-single">
                {/* Quick Visuals */}
                <div className="pmc-card">
                  <div className="pmc-card-head">
                    <div className="pmc-card-title">Quick Visuals</div>
                    <div className="pmc-muted small">Live insights</div>
                  </div>

                  <div className="pmc-card-body">
                    <div className="pmc-visuals-grid">
                      {/* Run composition */}
                      <div className="pmc-mini">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-bold">Run composition</div>
                          <span className="pmc-pill pmc-pill-muted">Total {runBreakdown.totalRuns}</span>
                        </div>

                        <div className="d-flex align-items-center gap-3 mt-2">
                          <div className="pmc-donut" style={runCompositionStyle}>
                            <div className="pmc-donut-center">{runBreakdown.totalRuns}</div>
                          </div>

                          <div className="pmc-muted small">
                            <div>
                              Boundaries <strong>{runBreakdown.boundaryRuns}</strong>
                            </div>
                            <div>
                              Extras <strong>{runBreakdown.extrasTotal}</strong>
                            </div>
                            <div>
                              Running <strong>{runBreakdown.otherRuns}</strong>
                            </div>
                            <div className="pmc-muted small mt-1">
                              {runBreakdown.totalFours} fours • {runBreakdown.totalSixes} sixes
                            </div>
                          </div>
                        </div>

                        <div className="pmc-compbar" aria-label="Run composition bar">
                          <span className="pmc-bounds" style={{ width: `${boundaryPct}%` }} />
                          <span className="pmc-extras" style={{ width: `${extrasPct}%` }} />
                          <span className="pmc-other" style={{ width: `${otherPct}%` }} />
                        </div>
                      </div>

                      {/* Wickets + Run rate */}
                      <div className="pmc-mini">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-bold">Wickets</div>
                          <span className="pmc-pill pmc-pill-muted">{Number(score.wickets) || 0}/10</span>
                        </div>

                        <div
                          className="pmc-wicket-ring"
                          style={{
                            background: `conic-gradient(var(--pmc-danger) 0 ${wicketPct}%, rgba(0,0,0,.08) ${wicketPct}% 100%)`,
                          }}
                        >
                          <div className="pmc-wicket-center">{Number(score.wickets) || 0}</div>
                        </div>

                        <div className="d-flex justify-content-between pmc-muted small">
                          <span>
                            Overs <strong>{displayOvers}</strong>
                          </span>
                          <span>
                            {chaseSummary.show ? 'Req RR ' : 'RR '}
                            <strong>{chaseSummary.show ? chaseSummary.requiredRunRate : (summary.runRate ?? 0)}</strong>
                          </span>
                        </div>

                        <div className="mt-2">
                          <div className="d-flex justify-content-between pmc-muted small">
                            <span>{chaseSummary.show ? 'Chase pace' : 'Run rate pace'}</span>
                            <span>
                              <strong>{(chaseSummary.show ? Number(chaseSummary.requiredRunRate) : runRateValue).toFixed(2)}</strong> / {runRateMax}
                            </span>
                          </div>
                          <div className="pmc-bars mt-1">
                            <div
                              style={{
                                width: `${Math.min(((chaseSummary.show ? Number(chaseSummary.requiredRunRate) : runRateValue) / runRateMax) * 100, 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Top batters */}
                      <div className="pmc-mini">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-bold">Top batters</div>
                          <span className="pmc-pill pmc-pill-muted">Top {topBatters.length || 0}</span>
                        </div>

                        {topBatters.length ? (
                          <div className="d-flex flex-column gap-2 mt-2">
                            {topBatters.map((batter, idx) => {
                              const pct = Math.round((batter.runs / maxBatterRuns) * 100);
                              return (
                                <div key={`${batter.name}-${idx}`}>
                                  <div className="d-flex justify-content-between small">
                                    <span className="fw-semibold text-truncate" style={{ maxWidth: 170 }}>
                                      {batter.name}
                                    </span>
                                    <span className="pmc-muted">{batter.runs}</span>
                                  </div>
                                  <div className="pmc-bars mt-1">
                                    <div style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="pmc-muted mt-2">Batting stats will appear once play starts.</div>
                        )}
                      </div>

                      {/* Recent deliveries */}
                      <div className="pmc-mini">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-bold">Recent deliveries</div>
                          <span className="pmc-pill pmc-pill-muted">Last 8</span>
                        </div>

                        <div className="pmc-ball-row">
                          {recentEvents.length ? (
                            recentEvents.map((ev, i) => {
                              const type = ballChipType(ev);
                              const label = compactBallLabel(ev);
                              const overLabel = formatOverLabel(ev.over, ev);
                              const chipKey = ev?._id || ev?.id || `${overLabel}-${i}`;
                              const bowlerInitials = formatPublicInitials(ev.bowler);
                              const strikerInitials = formatPublicInitials(ev.striker);
                              const recentTitle =
                                bowlerInitials && strikerInitials ? `${bowlerInitials} to ${strikerInitials}` : undefined;
                              return (
                                <div key={chipKey} className={`pmc-ball ${type}`} title={recentTitle}>
                                  <span className="pmc-ball-label">{label}</span>
                                  <span className="pmc-ball-over">{overLabel}</span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="pmc-muted">No deliveries yet.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            ) : null}
            {activeTab === 'scorecard' ? <div className="mt-3">{renderScorecard()}</div> : null}
            {activeTab === 'commentary' ? <div className="mt-3">{renderCommentary()}</div> : null}
            {activeTab === 'stats' ? <div className="mt-3">{renderStats()}</div> : null}
            {activeTab === 'summary' ? <div className="mt-3">{renderSummary()}</div> : null}
            {activeTab === 'squads' ? <div className="mt-3">{renderSquads()}</div> : null}
            {activeTab === 'awards' ? <div className="mt-3">{renderAwards()}</div> : null}
            {activeTab === 'info' ? <div className="mt-3">{renderInfo()}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PublicMatchViewPage;
